// Reusable BackToPaymentMethodButton component

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import CustomStepper2Step from "components/ui-component/CustomStepper2Step";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { CardIcon } from "icons/payments/Card";
import { BankIconSm } from "icons/payments/BankIconSm";
import { PaypalIcon } from "icons/dashboard/paypal";
import { CashAppIcon } from "icons/dashboard/cashAppIcon";
import moment from "moment";
import {
  addCard,
  oldPaymentMethodsGet,
  useBankData,
  useCardData,
  useHasLoadedPaymentMethods,
  useOldWalletData,
} from "store/slices/paymentMethodSlice";
import { valuesProps } from "types/paymentMethods";
import Drawer from "components/ui-component/SideDrawer";
import expiryDateValidation, {
  cardToShortname,
  checkAddress,
  checkBankManualFormOrPlaid,
  checkDefaultMethodExist,
  checkZip,
  formatCurrency,
  formatExpirationDate,
  getBankName,
  getCardType,
  getGeneralDetailMarkers,
  getMethod,
  getMethodCode,
  getOperatingSystem,
  shortToCardName,
  validateCard,
} from "utils/HelperFunctions";
import {
  getIntent,
  fetchPaymentDetails,
  makeGooglePay,
  paymentDownload,
  payPalOneTimeCreate,
  payPalOneTimeExecute,
  storePayPalOrder,
  verifyWalletPay,
  wirePaymentProcess,
  convFeeGet,
  updatePaymentPermission,
  oneTimeMakePayment,
  createPaypalToken,
  createPaypalPaymentToken,
  getPlaidLinkToken,
  getPlaidPaymentToken,
  duplicatePaymentGet,
  resetDuplicateOrder,
  resetCheckDuplicatePayment,
  setIsPaymentProcessing,
} from "store/slices/paymentsSlice";
import {
  addNotification,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";
import {
  setIsDashPreview,
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { goToInvalid } from "utils/invalidRoute";
import Button from "components/form/Button";
import RetryPaymentSuccess from "./RetryPaymentSuccess";
import RetryPaymentFailed from "./RetryPaymentFailed";
import Stepper from "components/ui-component/Stepper";
import {
  PaymentMethodList,
  PaymentGateway,
  SettingsValue,
  FormRef,
} from "types/payments";
import WirePaymentSuccess from "components/actionCenter/WirePaymentSuccess";
import CashAppForm from "components/paymentMethod/CashAppForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripeBankForm from "components/paymentMethod/StripeBankForm";
import BankPaymentButton from "./BankPaymentButton";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  useIsLogin,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import FailedPaymentComponent from "./FailedPaymentComponent";
import CardPaymentButton from "./CardPaymentButton";
import DisclosureInitial from "components/makePayment/DisclosureInitial";
import DisclosureFinal from "components/makePayment/DisclosureFinal";
import AmazonPay from "icons/dashboard/AmazonPay";
import FailedPaymentPreview from "./FailedPaymentPreview";
import AmazonPayForm from "components/paymentMethod/AmazonPayForm";
import { decode } from "utils/HelperFunctions";
import {
  decodeAndValidatePublicLink,
  isInvalidPublicLinkResult,
} from "utils/publicLinkValidation";
import { useMediaQuery } from "react-responsive";
import { initialValues } from "utils/countryAddressList";
import { Payload } from "pages/makePayment/paymentTypes";
import { PlaidLinkError, usePlaidLink } from "react-plaid-link";
import CustomModal from "components/ui-component/CustomModal";
import { FailedPaymentProps } from "../../types/FailedPaymentTypes";
import FailedPaymentHeader from "./FailedPaymentHeader";
import { RootState } from "store/store";
import StripeCardPaymentCheckoutForm from "components/paymentMethod/StripeCardPaymentCheckoutForm";
import { CardFormCheckType, PhoneInput } from "types/common";
import { CloseIcon } from "icons/payments/CloseIcon";
import { FailedAgreementIcon } from "icons/action/FailedIcon";
import { useDefaultPaymentMethodView } from "hooks/useDefaultPaymentMethodView";
import { useNavigationGuard } from "hooks/useNavigationGuard";
import { resolveStripePublishableKey } from "utils/stripePublishableKey";

const LoadingSpinner = () => (
  <div className="w-full h-full flex justify-center items-center">
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
  </div>
);

const BackToPaymentMethodButton = ({ onClick }: { onClick: () => void }) => (
  <p className="lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium">
    <span className="cursor-pointer" onClick={onClick}>
      Back to payment method
    </span>
  </p>
);

const FailedPayment: React.FC<FailedPaymentProps> = (props) => {
  const { id, setId, show, setShow, fetchDetails, onDirtyChange } = props;
  // Dynamic Stripe promises for each payment method
  const { paymentPermission, isPaymentProcessing } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [cardStripePromise, setCardStripePromise] = useState<any>(null);
  const [bankStripePromise, setBankStripePromise] = useState<any>(null);
  const [walletStripePromise, setWalletStripePromise] = useState<any>(null);
  const [amazonStripePromise, setAmazonStripePromise] = useState<any>(null);

  const { clusterId: cluster_id } = useParams();

  useEffect(() => {
    const cardKey = resolveStripePublishableKey(paymentPermission, "CC");
    const bankKey = resolveStripePublishableKey(paymentPermission, "ACH");
    const cashAppKey = resolveStripePublishableKey(paymentPermission, "CAPP");
    const amazonKey = resolveStripePublishableKey(paymentPermission, "AMZP");

    setCardStripePromise(cardKey ? loadStripe(cardKey) : null);
    setBankStripePromise(bankKey ? loadStripe(bankKey) : null);
    setWalletStripePromise(cashAppKey ? loadStripe(cashAppKey) : null);
    setAmazonStripePromise(amazonKey ? loadStripe(amazonKey) : null);
  }, [paymentPermission]);
  const isLogin = useIsLogin();
  const initialState = {
    filterParamValue: "",
    filterParam: "search",
    page: 1,
    state: "AL",
    country: "US",
    isrecurring: "oneTime",
    agreementType: "new",
    type: "Amount",
    amount: "0.00",
    new_offer_type: "Amount",
    paymentPlan: { start_date: moment().format("MM/DD/YYYY") },
    selectedCard: {},
    selectedBank: {},
  };

  const defaultPaymentMethodList = [
    {
      name: "Card",
      icon: <CardIcon />,
    },
    {
      name: "Bank",
      icon: <BankIconSm />,
    },
  ];

  const isLessThan680 = useMediaQuery({ query: "(max-width:680px)" });
  const cardList = useCardData();
  const bankList = useBankData();
  const oldWalletData = useOldWalletData();
  const hasLoadedPaymentMethods = useHasLoadedPaymentMethods();

  const accountLeadData = useLeadData();
  const portalSettings = usePortalSettingsData();
  const cashAppRef = useRef<FormRef>(null);
  const bankRef = useRef<FormRef>(null);
  const cardRef = useRef<FormRef>(null);
  const amazonRef = useRef<FormRef>(null);
  const cashAppRef1 = useRef<FormRef>(null);
  const bankRef1 = useRef<FormRef>(null);
  const cardRef1 = useRef<FormRef>(null);
  const amazonRef1 = useRef<FormRef>(null);
  const [dataPayment, setDataPayment] = useState<any>(initialState);
  const [initialDisclosureAccept, setInitialDisclosureAccept] =
    useState<boolean>(false);
  const [completedSteps, setCompletedSteps] = useState<any>([0]);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [failedReason, setFailedReason] = useState<any>(null);
  const [isAddNewCard, setIsAddNewCard] = useState<boolean>(false);
  const [paymentMethodList, setPaymentMethodList] =
    useState<PaymentMethodList[]>();
  const { conFee, isFeeLoading, companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [selectedMethod, setSelectedMethod] = useState<
    "Card" | "Bank" | "PayPal" | "Cash App Pay" | string
  >("Card");
  const [savedCardsEmail, setSavedCardsEmail] = useState<string>("");
  const userData = useUserData();
  const dispatch = useAppDispatch();
  const accountData = userData?.data?.accountData;
  const [paymentDetails, setPaymentDetails] = useState<any>({});
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const {
    payPalOrder,
    isDuplicateOrders,
    duplicateOrdersCount,
    checkedDuplicatePayment,
  } = useAppSelector((state: RootState) => state.payment);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(false);
  const [amount, setAmount] = useState("");
  const [amountDisplay, setAmountDisplay] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false);
  const [paymentProcess, setPaymentProcess] = useState<string | null>(null);
  const [isWirePayment, setIsWirePayment] = useState<boolean>(false);
  const [isBTNLoading, setIsBTNLoading] = useState<boolean>(false);
  const [isSecretLoading, setIsSecretLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const showFinalDisclosure = useShowFinalDisclosure();
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const [isManualForm, setIsManualForm] = useState<boolean>(true);
  const [wireDetails, setWireDetails] = useState({
    email: "",
    bank: "",
    routing: "",
    account: "",
    swift: "",
    dueDate: "",
  });
  const [values, setValues] = useState<valuesProps>(initialValues);
  const [hasTouchedForm, setHasTouchedForm] = useState(false);
  const [email, setEmail] = useState<string>(accountData?.email || "");
  const [cardNumber, setcardNumber] = useState<string>("");
  const [iconType, setIconType] = useState<string>("");
  const [selectedState, setSelectedState] = useState<any>(undefined);
  const [isDateErr, setIsDateErr] = useState<boolean>(false);
  const [bankName, setBankName] = useState<any>("");
  const [bank, setBank] = useState<any>("");
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const [isRingLoading, setIsRingLoading] = useState<boolean>(false);
  const [numberNotmatchedError, setnumberNotmatchedError] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Set when the payment details cannot be fetched. Without it the panel
  // carried on into the disclosure and method steps with no amount, no fee and
  // no payment method, giving the customer no clue anything had gone wrong.
  const [loadError, setLoadError] = useState<boolean>(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLinkTokenLoading, setIsLinkTokenLoading] = useState<boolean>(false);
  const [isErr, setIsErr] = useState(-1);
  const [cardErrMsg, setCardErrMsg] = useState("");
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [futurePayment, setFuturePayment] = useState<boolean>(false);
  const [googleTokenData, setGoogleTokenData] = useState({});
  const [clientSecret, setClientSecret] = useState<any>(null);
  const [bankSecret, setBankSecret] = useState<any>("");
  const [isUpdateBTNLoading, setIsUpdateBTNLoading] = useState<boolean>(false);
  const [applePayOrder, setApplePayOrder] = useState<any>({});
  const [isApplePay, setIsApplePay] = useState<boolean>(false);
  const [applePaySecret, setApplePaySecret] = useState<any>("");
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isPreviewId, setIsPreviewId] = useState<any>(null);
  const [showNavigationModal, setShowNavigationModal] =
    useState<boolean>(false);
  const [pendingGuardAction, setPendingGuardAction] = useState<
    "navigation" | "close" | null
  >(null);
  // Company logo shown inside this drawer's receipt preview. Kept local so the
  // recovery flow never overwrites the app-wide sidebar logo / browser favicon.
  const [companyLogo, setCompanyLogo] = useState<string>("");
  const [updatedData, setUpdatedData] = useState<any>();
  const [customerData, setCustomerData] = useState<any>({});
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });
  const [isDetailsGet, setIsDetailsGet] = useState<boolean>(false);
  const [formCheck, setFormCheck] = useState<CardFormCheckType>({
    card: false,
    expiry: false,
    cvv: false,
  });
  const [formEmptyCheck, setFormEmptyCheck] = useState<CardFormCheckType>({
    card: true,
    expiry: true,
    cvv: true,
  });

  useEffect(() => {
    // Always hand the flag back when this preview goes away or the screen
    // grows past the mobile breakpoint. Without that, navigating away with
    // the preview open left it stuck on, and the layout kept the mobile
    // header and footer hidden on every page.
    if (!isLessThan590) {
      dispatch(setIsDashPreview?.(false));
      return;
    }
    dispatch(setIsDashPreview?.(show));
    return () => {
      dispatch(setIsDashPreview?.(false));
    };
  }, [show, isLessThan590, isPreview]);

  const getPaymentDetails = async () => {
    const isFailedPaymentRoute =
      location.pathname.startsWith("/payments/failed/");
    const handleInvalidLink = () => {
      if (isFailedPaymentRoute) {
        goToInvalid(navigate, "link");
        return;
      }

      setIsLoading(false);
      setLoadError(true);
    };

    if (isFailedPaymentRoute && !decodeAndValidatePublicLink(id, "pid_")) {
      handleInvalidLink();
      return;
    }

    try {
      let params = {
        slug: id,
      };
      const res: any = await dispatch(fetchPaymentDetails(params)).unwrap();

      if (isInvalidPublicLinkResult(res)) {
        handleInvalidLink();
        return;
      }

      // An unsuccessful body is as much a failure as a rejected request — both
      // leave the panel with nothing to pay against.
      setLoadError(!res?.body?.success);
      if (res?.body?.success) {
        const {
          paymentHistoryDetails,
          companyLogoDetails,
          customerData,
          paymentPermission,
        } = res?.body?.data;
        setCustomerData(customerData);
        setCompanyLogo(companyLogoDetails?.logo || "");
        dispatch(updatePaymentPermission(paymentPermission));
        dispatch(
          updateUserData({
            ...userData?.data?.accountData,
            ...customerData?.data,
            lead_id: customerData?.lead_id,
          })
        );
        setPaymentDetails(paymentHistoryDetails);
        setValues({
          ...values,
          x_payment_method: paymentHistoryDetails?.payment_method,
        });
        // A recovery always needs the original failed payment's amount and
        // receipt address. These were previously populated only for an
        // already-succeeded payment, leaving preview-launched recoveries with
        // a $0/empty amount before the customer chose a method.
        setAmount(paymentHistoryDetails?.amount || "");
        setEmail(paymentHistoryDetails?.email || accountData?.email || "");
        if (paymentHistoryDetails?.payment_status === "Succeeded") {
          setPaymentProcess("true");
        }
        if (paymentHistoryDetails?.payment_method) {
          const { payment_method } = paymentHistoryDetails;
          setSelectedMethod(
            payment_method === "GP"
              ? "Card"
              : shortToCardName(payment_method) || "Card"
          );
        }
        if (
          paymentHistoryDetails?.customerid > 0 &&
          paymentHistoryDetails?.pendingFuturePayments > 0
        ) {
          setFuturePayment(true);
        }
      }
      setIsLoading(false);
    } catch (error) {
      if (isInvalidPublicLinkResult(error)) {
        handleInvalidLink();
        return;
      }

      setIsLoading(false);
      setLoadError(true);
      console.log("getPaymentDetails error =====", error);
    }
  };

  const retryLoadPaymentDetails = () => {
    setLoadError(false);
    setIsLoading(true);
    getPaymentDetails();
  };
  useEffect(() => {
    if (id && show) {
      getPaymentDetails();
    }
  }, [id, show]);

  useEffect(() => {
    if (paymentPermission) {
      let methodList = [];
      if (paymentPermission.cc_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "Card",
          icon: <CardIcon />,
        });
      }

      if (paymentPermission.ach_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "Bank",
          icon: <BankIconSm />,
        });
      }

      if (paymentPermission.pp_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "PayPal",
          icon: <PaypalIcon />,
        });
      }

      if (paymentPermission.capp_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "Cash App Pay",
          icon: <CashAppIcon />,
        });
      }

      if (paymentPermission.amzp_ext_availability === SettingsValue.YES) {
        methodList.push({
          name: "Amazon Pay",
          icon: <AmazonPay />,
        });
      }
      if (paymentPermission.cc_ext_availability === SettingsValue.NO) {
        setSelectedMethod(methodList?.[0]?.name);
      }

      setPaymentMethodList(methodList);
    } else {
      setPaymentMethodList(defaultPaymentMethodList);
    }
  }, [paymentPermission]);

  useEffect(() => {
    if (isAddNewCard) {
      const hasSelectedBank =
        dataPayment?.selectedBank &&
        Object.keys(dataPayment.selectedBank).length > 0;
      const hasSelectedCard =
        dataPayment?.selectedCard &&
        Object.keys(dataPayment.selectedCard).length > 0;
      const hasSelectedWallet =
        dataPayment?.selectedWallet &&
        Object.keys(dataPayment.selectedWallet).length > 0;

      if (hasSelectedBank || hasSelectedCard || hasSelectedWallet) {
        setIsDisabled(false);

        let method = null;

        if (hasSelectedBank) {
          method = "Bank";
        } else if (hasSelectedCard) {
          method = "Card";
        } else if (hasSelectedWallet) {
          method = "Wallet";
        }

        if (method && selectedMethod !== method) {
          setSelectedMethod(method);
        }
      } else {
        setIsDisabled(true);
      }
    } else {
      if (selectedMethod === "Card") {
        let addressCheck = checkAddress(
          portalSettings?.cc_billing_address,
          portalSettings?.cc_billing_addr_mandatory,
          values
        );
        let zipCheck = checkZip(values);
        let cardCheck = false;

        if (
          paymentPermission?.cc_gateway_name?.name === PaymentGateway.STRIPE
        ) {
          if (
            formCheck.card === false ||
            formCheck.expiry === false ||
            formCheck.cvv === false
          ) {
            cardCheck = false;
          } else {
            cardCheck = true;
          }
        } else {
          if (
            values?.x_card_num?.trim() !== "" &&
            values?.x_exp_date?.trim() !== "" &&
            values?.x_card_code?.trim() !== ""
          ) {
            cardCheck = true;
          } else {
            cardCheck = false;
          }
        }

        if (
          cardCheck &&
          values?.choldername?.trim() !== "" &&
          values?.x_country?.trim() !== "" &&
          addressCheck &&
          zipCheck &&
          email?.trim() !== "" &&
          isErr === -1
        ) {
          setIsDisabled(false);
        } else {
          if (!isDisabled) setIsDisabled(true);
        }
      } else if (
        selectedMethod === "Bank" &&
        paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE
      ) {
        let check = checkBankManualFormOrPlaid({
          manual_ach: portalSettings?.manual_ach,
          validate_bank_account: portalSettings?.validate_bank_account,
          isManualForm,
        });
        if (check) {
          setIsDisabled(false);
        } else {
          if (
            values?.routing_number?.trim() !== "" &&
            values?.routing_number &&
            values?.routing_number?.length === 9 &&
            !errMsg &&
            values?.account_number?.trim() !== "" &&
            values?.choldername?.trim() !== "" &&
            values?.r_account_number?.trim() !== "" &&
            email?.trim() !== ""
          ) {
            setIsDisabled(false);
          } else {
            if (!isDisabled) setIsDisabled(true);
          }
        }
      } else if (selectedMethod === "Cash App Pay") {
        setIsDisabled(false);
      } else if (selectedMethod === "Wire") {
        setIsDisabled(false);
      } else if (selectedMethod === "PayPal") {
        setIsDisabled(true);
      } else if (selectedMethod === "Google Pay") {
        setIsDisabled(true);
      } else if (selectedMethod === "Amazon Pay") {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    }
  }, [
    selectedMethod,
    values,
    email,
    dataPayment,
    isAddNewCard,
    isManualForm,
    portalSettings,
    paymentPermission,
    formCheck,
  ]);

  const findBankByRoutingNumber = async (routingNumber: string) => {
    setIsRingLoading(true);
    try {
      let bankObj = await getBankName(routingNumber);
      if (bankObj?.type === "success") {
        setBankName(bankObj?.bank_name);
        setBank(bankObj?.bank_name);
      } else {
        setErrMsg("Invalid routing number");
        setIsRingLoading(false);
      }
    } catch (error) {
      console.log("error =====", error);
      setErrMsg("Invalid routing number");
      setIsRingLoading(false);
    } finally {
      setIsRingLoading(false);
    }
  };

  const handleChangeCard = (e: any, name: string) => {
    setHasTouchedForm(true);
    if (name === "x_card_num") {
      let icon = getCardType(e.target.value) || iconType;
      if (icon === "amex") {
        if (e.target.value.length < 18) {
          const cardNumber = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3");
          setcardNumber(formattedCardNumber);
          setValues({ ...values, [name]: e.target.value });
        }
      } else {
        if (e.target.value.length < 20) {
          const cardNumber = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(?=\d)/g, "$1 ");
          setcardNumber(formattedCardNumber);
          setValues({ ...values, [name]: e.target.value });
        }
      }
    } else if (name === "x_card_code") {
      if (iconType === "amex") {
        if (e.target.value.length < 5) {
          setValues({ ...values, [name]: e.target.value });
        }
      } else {
        if (e.target.value.length < 4) {
          setValues({ ...values, [name]: e.target.value });
        }
      }
    } else if (name === "x_exp_date") {
      const inputValue = e.target.value;
      setIsDateErr(false);
      if (inputValue?.length < 6) {
        const isValidDate: any = expiryDateValidation(inputValue);
        const formattedDate = formatExpirationDate(inputValue);
        setValues({ ...values, [name]: formattedDate });
        !isValidDate?.isValid && inputValue?.length === 5 && setIsDateErr(true);
      }
    } else if (name === "x_country") {
      setValues({ ...values, [name]: e?.value });
    } else if (name === "x_state") {
      setValues({ ...values, [name]: e?.name });
    } else {
      setValues({ ...values, [name]: e.target.value });
    }
  };

  const handleChangeBank = (e: any, name: string) => {
    if (name === "routing_number") {
      if (e.target.value.length < 10) {
        setValues({ ...values, [name]: e.target.value });
        setBankName("");
        setBank("");
        setErrMsg(undefined);
        if (e.target.value.length === 9) {
          findBankByRoutingNumber(e.target.value);
        }
      }
    } else if (name === "r_account_number") {
      setnumberNotmatchedError(false);
      setValues({ ...values, [name]: e.target.value });
      if (values?.account_number?.length === e.target.value?.length) {
        setnumberNotmatchedError(values?.account_number !== e.target.value);
      }
    } else {
      setValues({ ...values, [name]: e.target.value });
    }
  };

  useEffect(() => {
    setValues((state) => ({
      ...state,
      x_payment_method: getMethodCode(selectedMethod),
    }));
  }, [selectedMethod]);

  useEffect(() => {
    if (!cardNumber) {
      setIconType("");
      setValues({ ...values, card_type: "" });
      return;
    } else {
      setIconType(getCardType(cardNumber));
      setValues({ ...values, card_type: getCardType(cardNumber) });
    }
  }, [cardNumber]);

  //payments

  //google pay
  const handleLegacyGooglePay = async (
    tokenData: any,
    total_amount: any,
    updateMethod: boolean = false
  ) => {
    setIsGoogleLoading(true);
    const { tokenizationData } = tokenData;
    let resp = JSON.parse(tokenizationData.token);
    let slug = decode(id || "")?.split("_");
    try {
      let payload = {
        slug: id || "",
        stripe_token: resp.id,
        phone_number: accountData?.phone || "",
        total_amount: paymentDetails?.amount + conFee,
        accountid: accountData?.accountid || "",
        module: "process_payment",
        email: email,
        isRetry: true,
        order_id: slug[0],
        clusterid: slug[1],
        address1: resp?.card?.address_line1 || "",
        address2: resp?.card?.address_line2 || "",
        city: resp?.card?.address_city || "",
        state: resp?.card?.address_state || "",
        zip: resp?.card?.address_zip || "",
        country: resp?.card?.address_country || "",
        updateMethod,
        description: "Online Payment",
        payment_note: "One-time",
      };
      let res: any = await dispatch(makeGooglePay(payload));
      if (res?.payload?.body.success) {
        setPaymentProcess("true");
        setEmail(res?.payload?.body?.data?.email);
        setAmountDisplay(res?.payload?.body?.data?.amount);
        let pay = {
          invoice_id: res?.payload?.body?.data?.invoice_id,
          emails: [res?.payload?.body?.data?.email],
        };
        await sendEmail(pay);
        setIsGoogleLoading(false);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "retry_succeeded",
            activity_type: "payment_activity",
            clusterid: slug[1],
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: paymentDetails?.amount,
            order_id: slug[0],
          })
        );
      } else {
        setFailedReason(res?.payload?.body?.data?.msg);
        setPaymentProcess("false");
        setIsGoogleLoading(false);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "retry_failed",
            activity_type: "payment_activity",
            clusterid: slug[1],
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: paymentDetails?.amount,
            order_id: slug[0],
          })
        );
      }
    } catch (error) {
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "payment_processed",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "retry_failed",
          activity_type: "payment_activity",
          clusterid: slug[1],
          communication_status: "",
          accounts_id: userData?.data?.accountData?.id,
          payment_amount: paymentDetails?.amount,
          order_id: slug[0],
        })
      );
      setIsGoogleLoading(false);
      console.log("handleGooglePay error=====", error);
    }
  };

  const handleGooglePay = async (
    paymentIntent: any,
    totalAmount: any,
    updateMethod: boolean = false
  ) => {
    if (paymentIntent?.tokenizationData) {
      await handleLegacyGooglePay(paymentIntent, totalAmount, updateMethod);
      return;
    }

    if (!paymentIntent?.id || !String(paymentIntent.id).startsWith("seti_")) {
      console.error("[Stripe Google Pay][failed-payment] invalid SetupIntent", {
        hasIntent: Boolean(paymentIntent?.id),
        intentType: paymentIntent?.id?.split("_")?.[0] || "unknown",
      });
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      setIsBTNLoading(false);
      if (futurePayment && updateMethod) {
        setIsUpdateBTNLoading(false);
      }
      return;
    }

    const slug = decode(id || "")?.split("_") || [];
    const retryAmount = Number(
      String(paymentDetails?.amount ?? totalAmount ?? 0).replace(/,/g, "")
    );

    try {
      setIsGoogleLoading(true);
      const payload = {
        slug: id || "",
        isRetry: true,
        order_id: slug[0],
        x_payment_method: "GP",
        amount: retryAmount,
        transaction_id: paymentIntent.id,
        accountid: accountData?.accountid,
        module: "process_payment",
        email: accountData?.email || email || "",
        lead_owner: accountData?.lead_owner,
        agent: accountData?.lead_owner,
        updateMethod,
        cluster_id: slug[1] || accountData?.clusterid,
        conv_fee: conFee,
        custom_conv: "on",
        description: "Online Payment",
        payment_note: "One-time",
        ivr_type: "CUST_PRTL",
      };

      console.info(
        "[Stripe Google Pay][failed-payment] verification requested",
        {
          hasTransactionId: true,
          intentType: paymentIntent.id.split("_")[0],
          paymentMethod: payload.x_payment_method,
          updateMethod,
        }
      );

      const res: any = await dispatch(verifyWalletPay(payload)).unwrap();
      const responseBody = res?.body;

      if (responseBody?.success) {
        const responseData = responseBody?.data;
        setPaymentProcess("true");
        setEmail(responseData?.email);
        setAmountDisplay(responseData?.total_amount ?? responseData?.amount);

        if (!responseData?.isDuplicate) {
          await sendEmail({
            invoice_id: responseData?.invoice_id,
            emails: [responseData?.email || accountData?.email],
          });
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "retry_succeeded",
              activity_type: "payment_activity",
              clusterid: slug[1] || accountData?.clusterid,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: retryAmount,
              order_id: responseData?.invoice_id,
            })
          );
        }
      } else {
        setFailedReason(
          responseBody?.data?.msg ||
            responseBody?.data?.message ||
            "Payment failed"
        );
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "retry_failed",
            activity_type: "payment_activity",
            clusterid: slug[1] || accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: retryAmount,
            order_id: slug[0],
          })
        );
      }
    } catch (error) {
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      console.error("[Stripe Google Pay][failed-payment] verification failed", {
        message: error instanceof Error ? error.message : String(error),
      });
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "payment_processed",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "retry_failed",
          activity_type: "payment_activity",
          clusterid: slug[1] || accountData?.clusterid,
          communication_status: "",
          accounts_id: userData?.data?.accountData?.id,
          payment_amount: retryAmount,
          order_id: slug[0],
        })
      );
    } finally {
      setIsGoogleLoading(false);
      setIsScreenLoading(false);
      setIsBTNLoading(false);
      if (futurePayment && updateMethod) {
        setIsUpdateBTNLoading(false);
      }
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
    }
  };

  //send email
  const sendEmail = async ({ shouldSendAll, ...values }: any) => {
    try {
      const validEmails = Array.isArray(values.emails)
        ? values.emails
            .map((email: any) =>
              typeof email === "string" ? email.trim() : ""
            )
            .filter(Boolean)
        : [];

      if (
        !values.invoice_id ||
        !userData?.data?.accountData?.accountid ||
        validEmails.length === 0
      ) {
        console.warn("sendEmail skipped: missing required values", {
          invoice_id: values.invoice_id,
          accountid: userData?.data?.accountData?.accountid,
          emails: values.emails,
        });
        return false;
      }

      let payload = {
        invoice_id: values.invoice_id,
        accountid: userData?.data?.accountData?.accountid,
        emails: validEmails,
        shouldSendAll: shouldSendAll,
      };
      await dispatch(paymentDownload(payload)).unwrap();
      // setPdfFile(res.body.data);
      return true;
    } catch (error) {
      console.log("sendEmail", error);
      // Reported rather than swallowed: the success screen needs to know, or a
      // failed send is indistinguishable from a delivered one.
      return false;
    }
  };

  //card bank wire payment
  const handleWirePayment = async () => {
    let slug = decode(id || "")?.split("_");
    try {
      let payload = {
        slug: id || "",
        isRetry: true,
        payment_gateway_id: "",
        amount: parseFloat(paymentDetails?.amount).toFixed(2),
        clusterid: slug[1],
        x_method: "WTO",
        lead_id: customerData?.lead_id,
        email: accountData?.email,
        accountid: accountData?.accountid,
        description: "Online Payment",
        payment_note: "One-time",
        ivr_type: "CUST_PRTL",
      };
      let res: any = await dispatch(wirePaymentProcess(payload)).unwrap();
      if (res.body.success) {
        setWireDetails({
          email: res?.body?.data?.email || "",
          bank: res?.body?.data?.bank_name || "",
          routing: res?.body?.data?.routing_number || "",
          account: res?.body?.data?.account_number || "",
          swift: res?.body?.data?.swift_code || "",
          dueDate:
            moment(
              moment().add(res?.body?.data?.wire_deadline || 0, "days")
            ).format("MM/DD/YYYY") || "",
        });
        const pay = {
          emails: [res?.body?.data?.email],
          invoice_id: slug[0],
        };
        await sendEmail(pay);
        setIsWirePayment(true);
        setPaymentProcess("true");
      } else {
        setFailedReason(res?.body?.data?.msg);
        setPaymentProcess("false");
      }
      setIsBTNLoading(false);
    } catch (error) {
      setIsBTNLoading(false);
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      console.log(" paymentViaWire error=====", error);
    }
  };

  //cashAppPayment
  const verifyCashAppPayment = async (
    method: string,
    amount: string,
    payment_intent: string,
    updateMethod: string,
    failedOrderId: string
  ) => {
    let slug = decode(failedOrderId || "")?.split("_");
    try {
      let payload = {
        slug: failedOrderId || "",
        isRetry: true,
        order_id: slug[0],
        x_payment_method: method,
        amount: amount,
        transaction_id: payment_intent,
        accountid: accountData?.accountid,
        module: "process_payment",
        email: accountData?.email || "",
        lead_owner: accountData?.lead_owner,
        agent: accountData?.lead_owner,
        updateMethod: updateMethod === "true",
        cluster_id: slug[1],
        conv_fee: conFee,
        custom_conv: "on",
        description: "Online Payment",
        ivr_type: "CUST_PRTL",
        payment_note: "One-time",
      };
      let res: any = await dispatch(verifyWalletPay(payload)).unwrap();
      if (res?.body?.success) {
        setPaymentProcess("true");
        setEmail(res?.body?.data?.email);
        setAmountDisplay(res?.body?.data?.total_amount);
        if (!res?.body?.data?.isDuplicate) {
          let pay = {
            invoice_id: res?.body?.data?.invoice_id,
            emails: [res?.body?.data?.email],
          };
          await sendEmail(pay);
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "retry_succeeded",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: amount,
              order_id: res?.body?.data?.invoice_id,
            })
          );
        }
      } else {
        setFailedReason(res?.body?.data?.msg);
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "retry_failed",
            activity_type: "payment_activity",
            clusterid: slug[1],
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: paymentDetails?.amount + conFee,
            order_id: slug[0],
          })
        );
      }
      setIsScreenLoading(false);
    } catch (error) {
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "payment_processed",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "retry_failed",
          activity_type: "payment_activity",
          clusterid: slug[1],
          communication_status: "",
          accounts_id: userData?.data?.accountData?.id,
          payment_amount: paymentDetails?.amount + conFee,
          order_id: slug[0],
        })
      );
      setIsScreenLoading(false);
      setPaymentProcess("false");
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
    }
  };

  //paypal intent create
  const createPayPalIntent = async () => {
    let slug = decode(id || "")?.split("_");
    if (paymentDetails?.customerid > 0) {
      setValues((state) => ({
        ...state,
        x_payment_method: "PP",
      }));
      setSelectedMethod("PayPal");
      setCurrentStep(2);
      setCompletedSteps([0, 1]);
    } else {
      try {
        let payload = {
          slug: id || cluster_id || "",
          lead_id: customerData?.lead_id,
          first_name: userData?.data?.accountData?.first_name,
          last_name: userData?.data?.accountData?.last_name,
          phone_number: userData?.data?.accountData?.phone,
          amount: Number(
            parseFloat(paymentDetails?.amount + conFee).toFixed(2)
          ),
          agent: userData?.data?.accountData?.lead_owner,
          lead_owner: userData?.data?.accountData?.lead_owner,
          clusterid: slug[1],
          accountid: userData.data.accountData.accountid,
          module: "process_payment",
          success_url:
            window.location.href +
            `?checked=false&status=success&drawer=open&failedOrderId=${id}&method=paypal&amount=${amount}`,
          cancel_url:
            window.location.href +
            `?checked=false&status=cancel&drawer=open&failedOrderId=${id}&method=paypal&amount=${amount}`,
          is_recurring: "N",
          isRetry: true,
          is_retry: true,
          order_id: slug[0],
          start_date: moment().format("MM/DD/YYYY"),
        };
        let res: any = await dispatch(payPalOneTimeCreate(payload));
        if (res?.payload?.body?.success) {
          await dispatch(
            storePayPalOrder({
              orderId: slug[0],
              amount: paymentDetails?.amount,
              url: res?.payload?.body?.result?.url,
            })
          );
          setValues((state) => ({
            ...state,
            x_payment_method: "PP",
          }));
          setSelectedMethod("PayPal");
          setCurrentStep(2);
          setCompletedSteps([0, 1]);
        } else {
          setPaymentProcess("false");
          setFailedReason("Payment failed");
        }
        return res;
      } catch (error) {
        console.log("createPayPalIntent error =====", error);
      }
    }
  };

  const handlePaypalPayment = async () => {
    try {
      const tokendata = {
        token: payPalOrder?.token,
      };
      let res: any = await dispatch(
        createPaypalPaymentToken(tokendata)
      ).unwrap();
      let slug = decode(id || "")?.split("_");
      if (res?.body?.success) {
        const payload = {
          lead_id: accountLeadData?.lead_id,
          cluster_id: payPalOrder?.clusterid,
          agent: userData?.data?.accountData?.lead_owner,
          balance: userData?.data?.accountData?.balance,
          lead_owner: userData?.data?.accountData?.lead_owner,
          x_payment_method: "PP",
          city: "",
          address1: "",
          address2: "",
          zipcode: "",
          state: "",
          country: "",
          first_name: userData?.data?.accountData?.first_name,
          last_name: userData?.data?.accountData?.last_name,
          accountid: userData?.data?.accountData?.accountid,
          amount: payPalOrder?.amount,
          amount_type: "amount",
          use_old_card: "1",
          description: "Online Payment",
          payment_note: "One-time",
          customer_device: getOperatingSystem(),
          card_type: "",
          old_cardnumber: "",
          old_cvv: "",
          old_expiry: "",
          old_4digit: "",
          old_type: "",
          old_zip: "",
          cvv: "",
          cexpiry: "",
          ccardnumber: "",
          choldername: "",
          aholdername: "",
          routing_number: "",
          account_number: "",
          bank_name: "",
          bank_address: "",
          cc_token: res?.body?.data?.cc_token,
          stripe_token: res?.body?.data?.cc_token,
          isRetry: true,
          order_id: payPalOrder?.orderId,
          cppid: res?.body?.data?.cppid,
          updateMethod: true,
        };
        const result: any = await dispatch(
          oneTimeMakePayment(payload)
        ).unwrap();
        if (result.body.success) {
          const pay = {
            emails: [email],
            invoice_id: slug[0],
          };
          await sendEmail(pay);
          setAmountDisplay(payPalOrder?.amount);
          setPaymentProcess("true");
          setIsScreenLoading(false);
          setIsLoading(false);
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "retry_succeeded",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: paymentDetails?.amount + conFee,
              order_id: slug[0],
            })
          );
        } else {
          setPaymentProcess("false");
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "retry_failed",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: paymentDetails?.amount + conFee,
              order_id: slug[0],
            })
          );
          setIsScreenLoading(false);
          setIsLoading(false);
        }
        setIsBTNLoading(false);
      } else {
        setPaymentProcess("false");
        setIsScreenLoading(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.log("handlePayPalPayment error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
    }
  };

  //paypal payment
  const handleApprovePayment = async ({ paymentId, payerID }: any) => {
    let slug = decode(id || "")?.split("_");
    try {
      let payload = {
        payerId: payerID,
        paymentId: paymentId,
        amount: payPalOrder.amount,
        order_id: payPalOrder.orderId,
        cluster_id: accountData?.clusterid,
        accountid: accountData?.accountid,
        updateMethod: payPalOrder?.updateMethod,
        isRetry: true,
        description: "Online Payment",
        payment_note: "One-time",
        slug: id || cluster_id || "",
      };
      let res: any = await dispatch(payPalOneTimeExecute(payload)).unwrap();
      if (res?.body?.success) {
        setPaymentProcess("true");
        setEmail(userData?.data?.accountData?.email);
        setAmountDisplay(payPalOrder.amount);
        let pay = {
          invoice_id: payPalOrder.orderId,
          emails: [userData?.data?.accountData?.email],
        };
        await sendEmail(pay);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "retry_succeeded",
            activity_type: "payment_activity",
            clusterid: accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: payPalOrder.amount,
            order_id: payPalOrder.orderId,
          })
        );
      } else {
        setFailedReason(res?.body?.data?.message);
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "retry_failed",
            activity_type: "payment_activity",
            clusterid: accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: payPalOrder.amount,
            order_id: payPalOrder.orderId,
          })
        );
      }
      setIsScreenLoading(false);
    } catch (error) {
      setFailedReason("Paymnet failed");
      setPaymentProcess("false");
      setIsScreenLoading(false);
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "payment_processed",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "retry_failed",
          activity_type: "payment_activity",
          clusterid: slug[1],
          communication_status: "",
          accounts_id: userData?.data?.accountData?.id,
          payment_amount: payPalOrder.amount,
          order_id: payPalOrder.orderId,
        })
      );
      console.log("handleApprovePayment error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
    }
  };

  const validateParams = (details: any) => {
    const method = details.method;

    if (method === "paypal") {
      return (
        (details.paymentId && details.PayerID && details.drawer) ||
        (details.approval_session_id &&
          details.approval_token_id &&
          details.drawer)
      );
    } else if (
      method === "cashapp" ||
      method === "card" ||
      method === "amazonPay" ||
      method === "bank"
    ) {
      return (details.setup_intent || details.payment_intent) && details.drawer;
    }
  };

  const getDetailsFromSearchParams = () => {
    setIsDetailsGet(true);
    setTimeout(() => {
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate({ search: "" });
    }, 0);
    return {
      method: searchParams.get("method") || null,
      drawer: searchParams.get("drawer") || null,
      amount: searchParams.get("amount") || null,
      payment_intent: searchParams.get("payment_intent") || null,
      setup_intent: searchParams.get("setup_intent") || null,
      updateMethod: searchParams.get("updateMethod") || null,
      failedOrderId: searchParams.get("failedOrderId") || null,
      token: searchParams.get("token") || null,
      status: searchParams.get("status") || null,
      approval_session_id: searchParams.get("approval_session_id") || null,
      approval_token_id: searchParams.get("approval_token_id") || null,
      paymentId: searchParams.get("paymentId") || null,
      PayerID: searchParams.get("PayerID") || null,
    };
  };

  const handleAnyMethodPayment = () => {
    let details = getDetailsFromSearchParams();
    const validParams = validateParams(details);
    if (!validParams) return;

    let checkedParams = searchParams.get("checked") || "false";
    searchParams.set("checked", "true");
    let checkAllNull = Object.values(details).every(
      (val) => val === null || val === "false"
    );
    if (checkAllNull) return;

    if (
      !isScreenLoading &&
      checkedParams === "false" &&
      details.method &&
      details.drawer &&
      details.amount
    ) {
      setIsScreenLoading(true);
      if (details.payment_intent || details.setup_intent) {
        let method = null;
        if (details.method === "cashapp") {
          method = "CAPP";
        } else if (details.method === "card") {
          method = "CC";
        } else if (details.method === "amazonPay") {
          method = "AMZP";
        } else if (details.method === "bank") {
          method = "ACH";
        }

        if (!method) {
          setIsScreenLoading(false);
          return;
        }

        verifyCashAppPayment(
          method,
          details.amount || "",
          details.payment_intent || "",
          details.updateMethod || "",
          details.failedOrderId || ""
        );
      } else if (
        details.token &&
        details.status &&
        checkedParams === "false" &&
        details.method === "paypal"
      ) {
        if (details.status === "cancel") {
          setPaymentProcess("false");
          setIsScreenLoading(false);
          dispatch(onSetFinalDisclosureLoading(false));
          dispatch(resetDuplicateOrder());
          dispatch(resetCheckDuplicatePayment());
          dispatch(onSetShowDisclosure(false));
          let slug = decode(details.failedOrderId || "")?.split("_");
          dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "retry_failed",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: payPalOrder.amount,
              order_id: slug[0],
            })
          );
        } else if (details.paymentId && details.PayerID) {
          handleApprovePayment({
            paymentId: details.paymentId,
            payerID: details.PayerID,
          });
        }
      } else if (
        details.approval_session_id &&
        details.approval_token_id &&
        details.status &&
        checkedParams === "false" &&
        details.method === "paypal"
      ) {
        handlePaypalPayment();
        setShow(true);
      }
    }
  };

  useEffect(() => {
    if (
      isDetailsGet ||
      searchParams.get("checked") === "true" ||
      !searchParams.has("failedOrderId") ||
      !searchParams.get("method") ||
      !show
    ) {
      return;
    }

    handleAnyMethodPayment();
  }, [id]);

  const onOldCardDataGet = async () => {
    let slug = decode(id || "")?.split("_");
    try {
      let payload = {
        accountid: userData?.data?.accountData?.accountid,
        accounts_id: userData?.data?.accountData?.id,
        cluster_id: slug[1],
      };
      await dispatch(oldPaymentMethodsGet(payload)).unwrap();
    } catch (error: any) {
      console.log("old card data get erro:-->", error?.message);
    }
  };

  useEffect(() => {
    if (isLogin && show) {
      onOldCardDataGet();
    }
  }, [show]);

  useDefaultPaymentMethodView({
    cardList,
    bankList,
    walletList: oldWalletData,
    paymentMethodList,
    paymentPermission,
    isLogin,
    hasLoadedPaymentMethods,
    isActive: show,
    setShowSavedMethods: setIsAddNewCard,
    setDataPayment,
    selectedMethod,
    setSelectedMethod,
  });

  const onClose = () => {
    setId("");
    setShow(false);
    setCurrentStep(1);
    setCompletedSteps([0]);
    setLoadError(false);
    setPaymentDetails({});
    setValues(initialValues);
    setHasTouchedForm(false);
    setCardErrMsg("");
    setBank("");
    setBankName("");
    setAmount("");
    setAmountDisplay("");
    setEmail("");
    setcardNumber("");
    setSelectedMethod("Card");
    setPaymentProcess(null);
    setIsLoading(true);
    setIsScreenLoading(false);
    setIsBTNLoading(false);
    setIsUpdateBTNLoading(false);
    setIsAddNewCard(false);
    setClientSecret(null);
    setBankSecret("");
    setFuturePayment(false);
    dispatch(onSetShowDisclosure(false));
    dispatch(onSetFinalDisclosureLoading(false));
    dispatch(resetDuplicateOrder());
    dispatch(resetCheckDuplicatePayment());
    setIsUpdate(false);
    setInitialDisclosureAccept(false);
    dispatch(storePayPalOrder({}));
    if (paymentProcess && fetchDetails) {
      fetchDetails();
    }
  };

  const isRecoveryPaymentProcessing =
    show &&
    !paymentProcess &&
    (isPaymentProcessing ||
      isScreenLoading ||
      isGoogleLoading ||
      isBTNLoading ||
      isUpdateBTNLoading);

  const shouldBlockRecoveryNavigation = useCallback(
    () => isRecoveryPaymentProcessing,
    [isRecoveryPaymentProcessing]
  );
  const handleRecoveryNavigationAttempt = useCallback(() => {
    setPendingGuardAction("navigation");
    setShowNavigationModal(true);
  }, []);

  const { allowPendingNavigation, cancelPendingNavigation } =
    useNavigationGuard({
      shouldBlock: shouldBlockRecoveryNavigation,
      onNavigationAttempt: handleRecoveryNavigationAttempt,
      enabled: show,
    });

  const requestClose = () => {
    if (isRecoveryPaymentProcessing) {
      setPendingGuardAction("close");
      setShowNavigationModal(true);
      return;
    }

    onClose();
  };

  const handleConfirmLeave = () => {
    const action = pendingGuardAction;

    setIsScreenLoading(false);
    setIsGoogleLoading(false);
    setIsBTNLoading(false);
    setIsUpdateBTNLoading(false);
    dispatch(setIsPaymentProcessing(false));
    setShowNavigationModal(false);
    setPendingGuardAction(null);

    if (action === "close") {
      onClose();
      return;
    }

    window.setTimeout(() => allowPendingNavigation(), 250);
  };

  const handleCancelLeave = () => {
    setShowNavigationModal(false);
    setPendingGuardAction(null);
    cancelPendingNavigation();
  };

  // Nothing is worth warning about until the customer has actually put
  // something in — opening the panel and closing it straight away should not
  // prompt. This tracks their own edits rather than comparing `values` against
  // initialValues, because the panel prefills `values` from the account as soon
  // as the payment details load, which would mark it dirty before they had
  // typed anything. Reaching the confirmation step counts too: there are
  // choices behind them by then.
  const hasUnsavedInput = currentStep > 1 || hasTouchedForm;

  useEffect(() => {
    onDirtyChange?.(hasUnsavedInput);
  }, [hasUnsavedInput, onDirtyChange]);

  // "Update payment method" on the failure screen means "pick a different
  // method", not "start this drawer over". The customer stays past the
  // disclosure they already accepted for this session (resetting
  // initialDisclosureAccept would drop them back on the notice and make them
  // re-tick it), and step 1 has to render as the step they are on rather than
  // a completed one — hence clearing completedSteps alongside currentStep.
  const handleBackToStart = () => {
    setCurrentStep(1);
    setCompletedSteps([0]);
    setValues(initialValues);
    setHasTouchedForm(false);
    setCardErrMsg("");
    setBank("");
    setBankName("");
    setAmount("");
    setcardNumber("");
    setSelectedMethod("Card");
    setPaymentProcess(null);
    setIsBTNLoading(false);
    setIsUpdateBTNLoading(false);
    setIsApplePay(false);
    setApplePayOrder({});
    setApplePaySecret("");
    dispatch(onSetShowDisclosure(false));
    dispatch(onSetFinalDisclosureLoading(false));
    dispatch(resetDuplicateOrder());
    dispatch(resetCheckDuplicatePayment());
  };

  const handleBackStepClick = (e?: any) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    // Defer step switch to avoid pointer-up click-through on newly rendered inputs.
    setTimeout(() => {
      setCurrentStep(1);
      setCompletedSteps([0]);
    }, 0);
  };

  //createPayload for card and bank
  const createPayload = (values: any) => {
    const { lead_id } = customerData || {};
    let slug = decode(id || "")?.split("_");
    const { lead_owner, balance, first_name, last_name, accountid } =
      accountData || {};

    try {
      let payload: Payload;
      const {
        x_payment_method,
        x_city,
        x_address,
        address2,
        x_zip,
        x_state,
        x_country,
        email,
        card_type,
        x_card_num,
        x_card_code,
        x_exp_date,
        choldername,
        routing_number,
        account_number,
        bankName,
        bank,
        cc_token,
        cc_token2,
        stripe_token,
        updateMethod,
        cpp_id,
        pc_atid,
        iconType,
      } = values;

      payload = {
        lead_id,
        cluster_id: slug[1],
        agent: lead_owner,
        balance,
        lead_owner: lead_owner,
        x_payment_method,
        city: x_city,
        address1: x_address,
        address2,
        zipcode: x_zip,
        state: x_state,
        country: x_country,
        first_name,
        last_name,
        accountid,
        amount: paymentDetails?.amount + conFee,
        email,
        amount_type: "amount",
        use_old_card: "1",
        description: "Online Payment",
        payment_note: "One-time",
        customer_device: getOperatingSystem(),
        card_type: card_type ?? iconType ?? "",
        old_cardnumber: x_card_num ? x_card_num?.replaceAll(" ", "") : "",
        old_cvv: x_card_code ? x_card_code : "",
        old_expiry: x_exp_date ? x_exp_date : "",
        old_4digit: x_card_num ? x_card_num?.slice(-4) : "",
        old_type: card_type ? card_type : "",
        old_zip: x_zip ? x_zip : "",
        cvv: x_card_code ? x_card_code : "",
        cexpiry: x_exp_date ? x_exp_date : "",
        ccardnumber: x_card_num ? x_card_num?.replaceAll(" ", "") : "",
        choldername: choldername ? choldername : "",
        aholdername: choldername ? choldername : "",
        routing_number: routing_number ? routing_number : "",
        account_number: account_number ? account_number : "",
        bank_name: bankName ? bankName : "",
        bank_address: bank ? bank : "",
        cc_token: cc_token ? cc_token : "",
        cc_token2: cc_token2 ? cc_token2 : "",
        stripe_token: stripe_token ? stripe_token : "",
        isRetry: true,
        order_id: slug[0],
        cppid: cpp_id,
        pc_atid: pc_atid,
        updateMethod: updateMethod,
      };
      return payload;
    } catch (error) {
      setIsBTNLoading(false);
      setIsUpdateBTNLoading(false);
      console.log("createPayload error =====", error);
    }
  };

  //make payment for card and bank
  const processPayment = async (values: any) => {
    let slug = decode(id || "")?.split("_");
    try {
      let payload = await createPayload(values);
      const res: any = await dispatch(oneTimeMakePayment(payload)).unwrap();
      if (res.body.success) {
        const email_value =
          res?.body?.data?.email || values.email || accountData?.email || "";
        const pay = {
          emails: [email_value],
          invoice_id: slug[0],
        };
        await sendEmail(pay);
        setEmail(email_value);
        setAmountDisplay(paymentDetails?.amount + conFee);
        setPaymentProcess("true");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "retry_succeeded",
            activity_type: "payment_activity",
            clusterid: slug[1],
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: paymentDetails?.amount + conFee,
            order_id: slug[0],
          })
        );
      } else {
        setPaymentProcess("false");
        setFailedReason(res?.body?.data?.msg);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "retry_failed",
            activity_type: "payment_activity",
            clusterid: slug[1],
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: paymentDetails?.amount + conFee,
            order_id: slug[0],
          })
        );
      }
      setIsBTNLoading(false);
      if (futurePayment && values?.updateMethod) {
        setIsUpdateBTNLoading(false);
      }
    } catch (error) {
      setIsBTNLoading(false);
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "payment_processed",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "retry_failed",
          activity_type: "payment_activity",
          clusterid: slug[1],
          communication_status: "",
          accounts_id: userData?.data?.accountData?.id,
          payment_amount: paymentDetails?.amount + conFee,
          order_id: slug[0],
        })
      );
      if (futurePayment && values?.updateMethod) {
        setIsUpdateBTNLoading(false);
      }
      console.log("processPayment error =====", error);
    }
  };

  const handleApplePay = async (updateMethod: boolean = false) => {
    let slug = decode(id || "")?.split("_");
    try {
      const { event, stripe, elements } = applePayOrder;
      // handleActions: false means Stripe hands an SCA challenge back to us
      // instead of running it, so the requires_action step below has to finish
      // it. This mirrors handleConfirmPayment in ApplePayButton.
      let { error: stripeError, paymentIntent } =
        await stripe.confirmCardPayment(
          applePaySecret,
          {
            payment_method: event.paymentMethod.id,
          },
          { handleActions: false }
        );

      if (!stripeError && paymentIntent?.status === "requires_action") {
        // This is a payment intent, so the challenge must be completed with
        // confirmCardPayment. confirmCardSetup is the setup-intent call and
        // Stripe rejects it against a payment-intent secret.
        const actionResult = await stripe.confirmCardPayment(applePaySecret);
        stripeError = actionResult?.error;
        paymentIntent = actionResult?.paymentIntent ?? paymentIntent;
      }

      if (stripeError || !paymentIntent) {
        setPaymentProcess("false");
        setFailedReason(
          stripeError?.message || JSON.stringify(stripeError ?? {})
        );
        setIsScreenLoading(false);
      } else {
        let payload = {
          isRetry: true,
          slug: id,
          order_id: slug[0],
          x_payment_method: "AP",
          amount: paymentDetails?.amount,
          transaction_id: paymentIntent,
          accountid: accountData?.accountid,
          module: "process_payment",
          email: accountData?.email || "",
          lead_owner: accountData?.lead_owner,
          agent: accountData?.lead_owner,
          updateMethod: updateMethod,
          cluster_id: slug[1],
          conv_fee: conFee,
          custom_conv: "on",
          description: "Online Payment",
          ivr_type: "CUST_PRTL",
          payment_note: "One-time",
        };
        let res: any = await dispatch(verifyWalletPay(payload));
        if (res?.payload?.body?.success) {
          setPaymentProcess("true");
          setEmail(res?.payload?.body?.data?.email);
          setAmountDisplay(
            res?.payload?.body?.data?.total_amount ||
              res?.payload?.body?.data?.amount
          );
          let pay = {
            invoice_id: res?.payload?.body?.data?.invoice_id,
            emails: [res?.payload?.body?.data?.email],
          };
          await sendEmail(pay);
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "retry_succeeded",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount:
                res?.payload?.body?.data?.total_amount ||
                res?.payload?.body?.data?.amount ||
                paymentDetails?.amount + conFee,
              order_id: slug[0],
            })
          );
        } else {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "retry_failed",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: paymentDetails?.amount + conFee,
              order_id: slug[0],
            })
          );
          setFailedReason(res?.payload?.body?.data?.msg);
          setPaymentProcess("false");
        }
        setIsScreenLoading(false);
      }
    } catch (error) {
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "payment_processed",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "retry_failed",
          activity_type: "payment_activity",
          clusterid: slug[1],
          communication_status: "",
          accounts_id: userData?.data?.accountData?.id,
          payment_amount: paymentDetails?.amount + conFee,
          order_id: slug[0],
        })
      );
      console.log("handleApplePay error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetShowDisclosure(false));
    }
  };

  //creare new card
  const createNewCard = async (updateMethod: boolean = false, values: any) => {
    let slug = decode(id || "")?.split("_");
    let payload = {
      x_card_num: values?.x_card_num?.replaceAll(" ", ""),
      x_exp_date: values?.x_exp_date,
      x_card_code: values?.x_card_code,
      choldername: values?.choldername,
      x_payment_method: getMethodCode(selectedMethod),
      x_city: values?.x_city || userData?.data?.accountData?.city || "",
      x_address:
        values?.x_address || userData?.data?.accountData?.address1 || "",
      address2: values?.address2 || userData?.data?.accountData?.address2 || "",
      x_zip: values?.x_zip || userData?.data?.accountData?.postal_code || "",
      x_state: values?.x_state || userData?.data?.accountData?.state || "",
      x_country:
        values?.x_country || userData?.data?.accountData?.country || "",
      routing_number: values?.routing_number,
      account_number: values?.account_number,
      bank_name: values?.bankName ?? bankName,
      swift_code: values?.swift_code,
      card_funding_type: values?.card_funding_type,
      default: "Y",
      default_profile: "N",
      // default: checkDefaultMethodExist(cardList, bankList, oldWalletData),
      // default_profile: checkDefaultMethodExist(
      //   cardList,
      //   bankList,
      //   oldWalletData
      // ),
      cluster_id: slug[1],
      accountid: accountData?.accountid,
      x_first_name: accountData?.first_name,
      x_last_name: accountData?.last_name,
      x_phone: accountData?.phone || "",
      x_email: accountData?.email || "",
      lead_id: customerData?.lead_id || "",
      gateway_id:
        selectedMethod === "Card"
          ? paymentPermission?.cc_gateway
          : paymentPermission?.ach_gateway || "",
      gateway_name:
        selectedMethod === "Card"
          ? paymentPermission?.cc_gateway_name?.name
          : paymentPermission?.ach_gateway_name?.name || "",
      account_holder_name: values?.choldername ?? "",
      card_type: iconType,
      accounts_id: accountData?.id,
    };
    try {
      const res: any = await dispatch(addCard(payload)).unwrap();
      if (res && res.body && res.body.success) {
        if (payload.x_payment_method === "WTO") {
          // await paymentViaWire();
        } else {
          await processPayment({
            ...payload,
            bankName: bank,
            cc_token: res?.body?.data?.paymentMethod.cc_token,
            cc_token2: res?.body?.data?.paymentMethod.cc_token2,
            stripe_token: res?.body?.data?.paymentMethod.cc_token,
            cpp_id: res?.body?.data?.paymentMethod?.id,
            updateMethod,
          });
        }
      } else {
        setIsBTNLoading(false);
        if (futurePayment && updateMethod) {
          setIsUpdateBTNLoading(false);
        }
        setIsErr(4);
        setCardErrMsg(
          "An error occurred while processing your payment. Try again later or with a different payment method."
        );
        // setPaymentProcess("false");
        // setFailedReason("Failed to Pay with this method");
      }
    } catch (error) {
      setIsBTNLoading(false);
      if (futurePayment && updateMethod) {
        setIsUpdateBTNLoading(false);
      }
      console.log("createNewCard error =====", error);
    }
  };

  //card google pay bank payment
  const handlePayment = async (updateMethod: boolean = false) => {
    try {
      if (futurePayment && updateMethod) {
        setIsUpdateBTNLoading(true);
      } else {
        setIsBTNLoading(true);
      }
      if (selectedMethod === "Google Pay") {
        await handleGooglePay(
          googleTokenData,
          paymentDetails?.amount + conFee,
          updateMethod
        );
      } else if (selectedMethod === "PayPal") {
        let slug = decode(id || "")?.split("_");
        if (paymentDetails?.customerid > 0) {
          try {
            const payload = {
              slug: id || "",
              isRetry: true,
              is_recurring: "Y",
              phone_number: userData?.data?.accountData?.phone,
              first_name: userData?.data?.accountData?.first_name,
              last_name: userData?.data?.accountData?.last_name,
              accountid: userData?.data?.accountData?.accountid,
              amount: amount,
              agent: userData?.data?.accountData?.lead_owner,
              lead_owner: userData?.data?.accountData?.lead_owner,
              lead_id: accountLeadData?.lead_id,
              clusterid: slug[1],
              success_url: `${
                window.location.href
              }?method=paypal&drawer=open&failedOrderId=${id}&amount=${amount}&checked=false&status=success&isRecurring=${true}`,
              cancel_url: `${
                window.location.href
              }?method=paypal&drawer=open&failedOrderId=${id}&amount=${amount}&checked=false&status=cancel&isRecurring=${true}`,
              start_date: new Date(),
            };
            let res: any = await dispatch(createPaypalToken(payload));
            if (res?.payload?.body?.success) {
              dispatch(
                storePayPalOrder({
                  url: res?.payload?.body?.result?.url,
                  orderId: paymentDetails?.id,
                  token: res?.payload?.body?.result?.token,
                  amount: paymentDetails?.amount,
                  clusterid: slug[1],
                  isRetry: updateMethod,
                })
              );
              window.location.href = res?.payload?.body?.result?.url;
            } else {
              setFailedReason(res?.payload?.body?.data?.msg);
            }
          } catch (error) {
            console.log("createPayPalIntent error =====", error);
          }
        } else {
          await dispatch(
            storePayPalOrder({
              ...payPalOrder,
              updateMethod,
              amount: paymentDetails?.amount,
            })
          );
          window.location.href = payPalOrder.url;
        }
      } else {
        if (!isAddNewCard) {
          await createNewCard(updateMethod, values);
        } else {
          let method = "CC";
          if (
            dataPayment?.selectedBank &&
            Object.keys(dataPayment.selectedBank).length > 0
          ) {
            method = "ACH";
          }
          if (
            dataPayment?.selectedWallet &&
            Object.keys(dataPayment.selectedWallet).length > 0
          ) {
            method = "WT";
          }

          let selectedData;
          if (method === "CC") {
            selectedData = dataPayment?.selectedCard;
          } else if (method === "WT") {
            selectedData = dataPayment?.selectedWallet;
            method = selectedData?.payment_method;
          } else {
            selectedData = dataPayment?.selectedBank;
          }

          const payload = {
            x_payment_method: method,
            email: savedCardsEmail || userData?.data?.accountData?.email || "",
            x_card_num: selectedData?.cc_4digit,
            x_card_code: selectedData?.cc_cvv,
            x_exp_date: selectedData?.cc_expiry,
            iconType: selectedData?.cc_type,
            routing_number: selectedData?.routing_number,
            account_number: selectedData?.account_number,
            cc_token: selectedData?.cc_token,
            cc_token2: selectedData?.cc_token2,
            stripe_token: selectedData?.cc_token,
            bankName: selectedData?.bank_name,
            x_city: selectedData?.billing_city,
            x_address: selectedData?.billing_address,
            address2: selectedData?.billing_address2,
            x_zip: selectedData?.billing_zip,
            x_state: selectedData?.billing_state,
            x_country: selectedData?.billing_country,
            choldername: selectedData?.account_name,
            cppid: selectedData?.id,
            updateMethod,
          };
          await processPayment(payload);
        }
      }
    } catch (error) {
      console.log("handlePayment error =====", error);
    }
  };

  // client secret for stripe financial bank and cashapp
  const getSecret = async (payment_method_types: string) => {
    let slug = decode(id || "")?.split("_");
    try {
      setIsSecretLoading(true);
      let amt = Number() + Number(paymentDetails?.amount);
      let payload = {
        name: (accountData?.first_name || "") + (accountData?.last_name || ""),
        email: accountData?.email,
        phone_number: accountData.phone,
        total_amount: Number((amt * 100).toFixed(2)),
        order_id: slug[0],
        // total_amount: amt,
        description: "Online Payment",
        payment_note: "One-time",
        currency: "usd",
        payment_method_types,
        isRecurring: false,
        stripe_fin: payment_method_types === "us_bank_account" ? "Y" : "N",
        accountid: userData?.data?.accountData?.accountid,
        cluster_id: slug[1],
        custom_conv: "on",
      };
      let res: any = await dispatch(getIntent(payload));
      if (res?.payload?.body?.success) {
        if (
          payment_method_types === "cashapp" ||
          payment_method_types === "amazon_pay"
        ) {
          setClientSecret(res?.payload?.body?.data?.client_secret);
        } else {
          setBankSecret(res?.payload?.body?.data?.client_secret);
        }
      }
      setIsSecretLoading(false);
    } catch (err) {
      console.log("err =====", err);
      setIsSecretLoading(false);
    }
  };

  const getPlaidToken = async () => {
    setIsLinkTokenLoading(true);
    try {
      let payload = {
        client_name:
          (companyDetails?.name || companyDetails?.company_name) ??
          "Palm Labs LLC",
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name?.trim(),
        email: userData?.data?.accountData?.email || email,
        clusterid: userData?.data?.accountData?.clusterid,
        slug: cluster_id || "",
      };
      let res: any = await dispatch(getPlaidLinkToken(payload)).unwrap();
      if (res?.body?.success) {
        setLinkToken(res?.body?.data?.link_token || null);
      }
      setIsLinkTokenLoading(false);
    } catch (error) {
      console.log("getPlaidToken error =====", error);
      setIsLinkTokenLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    if (
      selectedMethod === "Cash App Pay" &&
      !searchParams.has("payment_intent") &&
      !searchParams.has("setup_intent")
    ) {
      getSecret("cashapp");
    }

    if (
      selectedMethod === "Amazon Pay" &&
      !searchParams.has("payment_intent") &&
      !searchParams.has("setup_intent")
    ) {
      getSecret("amazon_pay");
    }

    if (
      selectedMethod === "Bank" &&
      !searchParams.has("payment_intent") &&
      !searchParams.has("setup_intent") &&
      paymentPermission?.ach_gateway_name?.name === PaymentGateway.STRIPE &&
      !bankSecret
    ) {
      getSecret("us_bank_account");
    }

    let check = checkBankManualFormOrPlaid({
      manual_ach: portalSettings?.manual_ach,
      validate_bank_account: portalSettings?.validate_bank_account,
      isManualForm,
    });

    if (
      selectedMethod === "Bank" &&
      paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE &&
      check
    ) {
      getPlaidToken();
    }
  }, [selectedMethod, isManualForm, portalSettings]);

  const handleOnClick = (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step);
      setCompletedSteps(Array.from({ length: step }, (_, i) => i));
    } else {
      return;
    }
  };

  useEffect(() => {
    const getConvFee = async () => {
      try {
        let params = {
          cluster_id: decode(id || "")?.split("_")[1],
          state:
            values?.x_state !== ""
              ? values?.x_state
              : userData?.data?.accountData?.state,
          x_method: getMethod(isAddNewCard, dataPayment, selectedMethod),
          phone_number: userData?.data?.accountData?.phone,
          x_amount: paymentDetails?.amount,
          lead_id: customerData?.lead_id,
          custom_conv: "on",
        };

        await dispatch(convFeeGet(params)).unwrap();
      } catch (error) {
        console.log("getConvFee error =====", error);
      }
    };
    if (paymentDetails?.amount > 0) {
      getConvFee();
    }
  }, [paymentDetails, selectedMethod, isAddNewCard, dataPayment]);

  //bank form options
  const bankFormOptions = {
    clientSecret: bankSecret,
    appearance: {
      variables: {
        fontFamily: "DM Sans, sans-serif",
        colorDanger: "#ff0000",
        fontSizeSm: "12px",
        fontSizeXs: "12px",
        colorText: "#19224c",
        colorTextSecondary: "#19224c",
        colorPrimary: "#1678f2",
        colorBackgroundDeemphasize10: "#dfe4ec",
        borderRadius: "6px",
      },
      rules: {
        ".Label": {
          color: "#8c90a5",
          fontWeight: "700",
          fontSize: "12px",
          textTransform: "uppercase",
        },
        ".Input::placeholder": {
          fontWeight: "400",
          fontSize: "12px",
          color: "#8c90a5",
        },
        ".Input": {
          border: "1px solid #dfe4ec",
          fontWeight: "400",
          fontSize: "12px",
          color: "#8c90a5",
        },
        ".Error": {
          fontSize: "12px",
          fontWeight: "400",
        },
        ".PickerItem": {
          border: "1px solid #dfe4ec",
          borderRadius: "6px",
        },
      },
    },
  };

  const checkPastAmount = async (method?: string) => {
    try {
      let payload = {
        accounts_id: userData?.data?.accountData?.id,
        payment_method: method ? method : cardToShortname(selectedMethod),
        amount: paymentDetails?.amount,
      };
      let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
      const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
      if (isDuplicateOrders && duplicateOrdersCount > 0) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const handleCheckDisclosureAndDuplicate = async (method?: string) => {
    try {
      let moveAhead = !checkedDuplicatePayment
        ? await checkPastAmount(method)
        : true;
      if (!moveAhead) {
        dispatch(onSetFinalDisclosureLoading(false));
        setIsLoading(false);
        return false;
      } else {
        if (
          (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
            portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT) &&
          !showFinalDisclosure
        ) {
          setIsLoading(false);
          dispatch(resetDuplicateOrder());
          dispatch(onSetFinalDisclosureLoading(false));
          dispatch(onSetShowDisclosure(true));
        } else {
          dispatch(resetDuplicateOrder());
          dispatch(resetCheckDuplicatePayment());
          return true;
        }
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const handleOnSubmit = async (isUpdate: boolean) => {
    if (!isUpdate) {
      setIsBTNLoading(true);
    } else {
      setIsUpdateBTNLoading(true);
    }
    let moveAhead = await handleCheckDisclosureAndDuplicate();
    if (!moveAhead) {
      setIsUpdate(isUpdate);
      setIsBTNLoading(false);
      setIsUpdateBTNLoading(false);
      return;
    }
    try {
      if (selectedMethod === "Wire") {
        await handleWirePayment();
      } else {
        if (isApplePay) {
          await handleApplePay(isUpdate);
        } else {
          await handlePayment(isUpdate);
        }
      }
    } catch (error) {
      console.log("error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const handleAccept = async () => {
    await dispatch(onSetFinalDisclosureLoading(true));
    try {
      if (isAddNewCard) {
        await handleOnSubmit(isUpdate);
      } else {
        switch (selectedMethod) {
          case "Card":
            if (
              paymentPermission?.cc_gateway_name?.name === PaymentGateway.STRIPE
            ) {
              if (isUpdate && cardRef.current) {
                await cardRef.current.callHandleClick();
              } else if (cardRef1.current) {
                await cardRef1.current.callHandleClick();
              }
            } else {
              await handleOnSubmit(isUpdate);
            }
            break;
          case "Amazon Pay":
            if (isUpdate && amazonRef.current) {
              await amazonRef.current.callHandleClick();
            } else if (amazonRef1.current) {
              await amazonRef1.current.callHandleClick();
            } else {
              await handleOnSubmit(isUpdate);
            }
            break;
          case "Bank":
            let check = checkBankManualFormOrPlaid({
              manual_ach: portalSettings?.manual_ach,
              validate_bank_account: portalSettings?.validate_bank_account,
              isManualForm,
            });
            if (
              paymentPermission?.ach_gateway_name?.name ===
                PaymentGateway.STRIPE ||
              check
            ) {
              if (isUpdate && bankRef.current) {
                await bankRef.current.callHandleClick();
              } else if (bankRef1.current) {
                await bankRef1.current.callHandleClick();
              }
            } else {
              await handleOnSubmit(isUpdate);
            }
            break;
          case "Cash App Pay":
            if (isUpdate && cashAppRef.current) {
              await cashAppRef.current.callHandleClick();
            } else if (cashAppRef1.current) {
              await cashAppRef1.current.callHandleClick();
            } else {
              await handleOnSubmit(isUpdate);
            }
            break;
          case "Wire":
            await handleOnSubmit(isUpdate);
            break;
          case "PayPal":
            await handleOnSubmit(isUpdate);
            break;
          case "Google Pay":
            await handleOnSubmit(isUpdate);
            break;
          default:
            console.log("selectedMethod =====", selectedMethod);
        }
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const generalMarkers = getGeneralDetailMarkers({
    conFee,
    paymentAmount: paymentDetails?.amount,
    nextDate: moment().format("MM/DD/YYYY"),
  });

  const handlePlaidBankPayment = async ({
    value,
    isCCTokenAvailable,
    updateMethod,
  }: {
    value: any;
    isCCTokenAvailable: boolean;
    updateMethod: boolean;
  }) => {
    try {
      if (futurePayment && updateMethod) {
        setIsUpdateBTNLoading(true);
      } else {
        setIsBTNLoading(true);
      }
      if (!isCCTokenAvailable) {
        let payload = {
          choldername: value?.choldername,
          x_city: value?.x_city,
          x_address: value?.x_address,
          address2: value?.address2,
          x_zip: value?.x_zip,
          x_state: value?.x_state,
          x_country: value?.x_country,
          routing_number: value?.routing_number,
          account_number: value?.account_number,
          bankName: value?.bankName,
          gateway_id: value?.gatewayId,
          gateway_name: value?.gateway_name,
          default: "N",
          pc_atid: value?.pc_atid,
        };
        await createNewCard(updateMethod, payload);
      } else {
        let payload = {
          x_payment_method: "ACH",
          email: value?.email,
          routing_number: value?.routing_number,
          account_number: value?.account_number,
          cc_token: value?.cc_token,
          cc_token2: value?.cc_token2,
          stripe_token: value?.cc_token,
          bankName: value?.bankName,
          x_city: value?.x_city,
          x_address: value?.x_address,
          address2: value?.address2,
          x_zip: value?.x_zip,
          x_state: value?.x_state,
          x_country: value?.x_country,
          choldername: value?.choldername,
          isRetry: true,
          cpp_id: value?.cppid,
          pc_atid: value?.pc_atid,
          updateMethod,
        };
        await processPayment(payload);
      }
    } catch (error) {
      console.log("handlePlaidBankPayment error =====", error);
    } finally {
      setIsUpdateBTNLoading(false);
      setIsBTNLoading(false);
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const verifyStripeCardPayment = async ({
    intent,
    email,
    error,
    orderId,
    principalAmount,
    updateMethod = false,
  }: {
    intent: string | null;
    email: string;
    error: any;
    orderId?: number;
    principalAmount?: number;
    updateMethod?: boolean;
  }) => {
    let slug = decode(id || "")?.split("_");

    if (error) {
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "payment_processed",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "retry_failed",
          activity_type: "payment_activity",
          clusterid: slug[1],
          communication_status: "",
          accounts_id: userData?.data?.accountData?.id,
          payment_amount: paymentDetails?.amount + conFee,
          order_id: slug[0],
        })
      );
      setFailedReason(error.message);
      setIsScreenLoading(false);
      setPaymentProcess("false");
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
    } else {
      try {
        let payload = {
          isRetry: true,
          slug: id || "",
          order_id: slug[0],
          x_payment_method: "CC",
          amount: principalAmount ?? amount,
          transaction_id: intent,
          accountid: accountData?.accountid,
          module: "process_payment",
          email: accountData?.email || "",
          lead_owner: accountData?.lead_owner,
          agent: accountData?.lead_owner,
          updateMethod: updateMethod,
          cluster_id: slug[1],
          conv_fee: conFee,
          custom_conv: "on",
          description: "Online Payment",
          ivr_type: "CUST_PRTL",
          payment_note: "One-time",
        };
        let res: any = await dispatch(verifyWalletPay(payload)).unwrap();
        if (res?.body?.success) {
          setPaymentProcess("true");
          setEmail(res?.body?.data?.email);
          setAmountDisplay(res?.body?.data?.total_amount);
          if (!res?.body?.data?.isDuplicate) {
            let pay = {
              invoice_id: res?.body?.data?.invoice_id,
              emails: [res?.body?.data?.email],
            };
            await sendEmail(pay);
            await dispatch(
              addNotification({
                initiated_by: "contact",
                event_type: "payment_processed",
                userid: userData?.data?.accountData?.leadData?.user_id,
                email: "",
                event_status: "retry_succeeded",
                activity_type: "payment_activity",
                clusterid: slug[1],
                communication_status: "",
                accounts_id: userData?.data?.accountData?.id,
                payment_amount: res?.body?.data?.total_amount || amount,
                order_id: res?.body?.data?.invoice_id,
              })
            );
          }
        } else {
          setFailedReason(res?.body?.data?.msg);
          setPaymentProcess("false");
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "retry_failed",
              activity_type: "payment_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: paymentDetails?.amount + conFee,
              order_id: slug[0],
            })
          );
        }
        setIsScreenLoading(false);
      } catch (error) {
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "retry_failed",
            activity_type: "payment_activity",
            clusterid: slug[1],
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: paymentDetails?.amount + conFee,
            order_id: slug[0],
          })
        );
        setIsScreenLoading(false);
        setPaymentProcess("false");
      } finally {
        dispatch(onSetFinalDisclosureLoading(false));
        dispatch(onSetShowDisclosure(false));
        dispatch(resetDuplicateOrder());
        dispatch(resetCheckDuplicatePayment());
      }
    }
  };

  return (
    <>
      <div
        // scrollbar-gutter keeps the scrollbar's space reserved at all times.
        // Stepping between 1 and 2 briefly overlaps both accordions, so the
        // panel overflowed for about 100ms and its scrollbar appeared and then
        // vanished — shunting every element 8px sideways and back.
        className={`relative w-full bg-white h-[var(--shell-vh)] overflow-y-auto [scrollbar-gutter:stable] lg:px-10 lg:pt-10  pt-5`}
      >
        {isLoading || isScreenLoading ? (
          <LoadingSpinner />
        ) : loadError ? (
          <>
            <div
              role="button"
              tabIndex={0}
              aria-label="Close failed payment recovery"
              className="absolute top-10 right-10 cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
              onClick={requestClose}
            >
              <CloseIcon />
            </div>
            <div
              className="h-full flex items-center justify-center lg:px-0 px-5"
              aria-label="Payment details could not be loaded"
            >
              <div className="w-full flex flex-col justify-center items-center gap-10">
                <FailedAgreementIcon aria-hidden="true" />
                <div className="w-full text-center text-xl text-txtPrimary font-medium">
                  We couldn’t load this payment
                </div>
                <div className="w-full text-center text-txtParagraph lg:text-lg">
                  Something went wrong while fetching the payment details.
                  Please try again.
                </div>
                <Button
                  text="Try again"
                  aria-label="Try loading the payment again"
                  className="text-base px-11"
                  onClick={retryLoadPaymentDetails}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {paymentProcess ? (
              paymentProcess === "true" ? (
                isWirePayment ? (
                  <WirePaymentSuccess
                    bankDetails={wireDetails}
                    orderId={decode(id || "")?.split("_")[0]}
                    isRetry={true}
                    sendEmail={sendEmail}
                    lead_id={customerData?.lead_id}
                  />
                ) : (
                  <>
                    <div
                      className="absolute top-10 right-10 cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
                      onClick={requestClose}
                    >
                      <CloseIcon />
                    </div>
                    <RetryPaymentSuccess
                      email={email}
                      invoiceId={
                        decode(id || "")?.split("_")[0]
                          ? (decode(id || "")?.split("_")[0] as string)
                          : payPalOrder?.orderId || ""
                      }
                      amount={amountDisplay}
                      sendEmail={sendEmail}
                      lead_id={customerData?.lead_id}
                      onClose={requestClose}
                    />
                  </>
                )
              ) : (
                <>
                  <div
                    className="absolute top-10 right-10 cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
                    onClick={requestClose}
                  >
                    <CloseIcon />
                  </div>
                  <RetryPaymentFailed
                    failedReason={failedReason}
                    handleBackToStart={handleBackToStart}
                  />
                </>
              )
            ) : (
              <>
                {/* Title */}
                <FailedPaymentHeader
                  onPreviewClick={() => {
                    setUpdatedData({
                      iconType,
                      isAddNewCard,
                      dataPayment,
                      values,
                      selectedMethod,
                    });
                    setIsPreview(true);
                    setIsPreviewId(decode(id || "")?.split("_")[0]);
                  }}
                  onClose={requestClose}
                />

                {/* Content */}

                {portalSettings?.state_disclosure_flag === SettingsValue.OFF ||
                initialDisclosureAccept ||
                !isLogin ? (
                  <>
                    {/* laptop desktop view */}
                    {bankSecret &&
                    selectedMethod === "Bank" &&
                    paymentPermission?.ach_gateway_name.name ===
                      PaymentGateway.STRIPE &&
                    !isAddNewCard &&
                    bankStripePromise ? (
                      <Elements
                        stripe={bankStripePromise}
                        options={bankFormOptions}
                      >
                        {/* before disclosure */}
                        <div
                          className={showFinalDisclosure ? "hidden none" : ""}
                        >
                          <div className="h-[92%] gap-7 lg:flex hidden">
                            <div className={`w-[10%] flex pt-2`}>
                              <CustomStepper2Step
                                currentStep={currentStep}
                                completedSteps={completedSteps}
                                className="!h-[60px]"
                                handleOnClick={handleOnClick}
                              />
                            </div>
                            <div className="flex flex-col gap-4 w-full">
                              <FailedPaymentComponent
                                order_id={id}
                                currentStep={currentStep}
                                isAddNewCard={isAddNewCard}
                                setIsAddNewCard={setIsAddNewCard}
                                dataPayment={dataPayment}
                                setDataPayment={setDataPayment}
                                walletList={oldWalletData}
                                setSavedCardsEmail={setSavedCardsEmail}
                                paymentMethodList={paymentMethodList}
                                setSelectedMethod={setSelectedMethod}
                                handleChangeCard={handleChangeCard}
                                setSelectedState={setSelectedState}
                                cardNumber={cardNumber}
                                selectedState={selectedState}
                                selectedMethod={selectedMethod}
                                iconType={iconType}
                                isDateErr={isDateErr}
                                handleChangeBank={handleChangeBank}
                                numberNotmatchedError={numberNotmatchedError}
                                bankName={bankName}
                                errMsg={errMsg}
                                isRingLoading={isRingLoading}
                                email={email}
                                setEmail={setEmail}
                                values={values}
                                setnumberNotmatchedError={
                                  setnumberNotmatchedError
                                }
                                paymentDetails={paymentDetails}
                                validateCard={() =>
                                  validateCard({
                                    values,
                                    iconType,
                                    setIsErr,
                                    setCardErrMsg,
                                    setErrMsg,
                                  })
                                }
                                isErr={isErr}
                                cardErrMsg={cardErrMsg}
                                setValues={setValues}
                                setGoogleTokenData={setGoogleTokenData}
                                setCurrentStep={setCurrentStep}
                                setCompletedSteps={setCompletedSteps}
                                createPayPalIntent={createPayPalIntent}
                                isGoogleLoading={isGoogleLoading}
                                setApplePaySecret={setApplePaySecret}
                                setApplePayOrder={setApplePayOrder}
                                setIsApplePay={setIsApplePay}
                                isDisabled={isDisabled}
                                isFeeLoading={isFeeLoading}
                                isSecretLoading={isSecretLoading}
                                futurePayment={futurePayment}
                                isManualForm={isManualForm}
                                setIsManualForm={setIsManualForm}
                                BankComp={
                                  <div className="pt-10">
                                    <StripeBankForm
                                      amount={paymentDetails?.amount}
                                      email={email}
                                      setEmail={setEmail}
                                      isSecretLoading={isLoading}
                                      isRetry={true}
                                      setIsDisable={setIsDisabled}
                                    />
                                  </div>
                                }
                              />
                              {currentStep === 2 &&
                                (futurePayment ? (
                                  <div className="flex flex-col lg:gap-6 gap-4 justify-center items-center pt-10">
                                    <div className="w-full flex lg:flex-row flex-col gap-4">
                                      <BankPaymentButton
                                        buttonText={`Pay ${
                                          formatCurrency(
                                            parseFloat(paymentDetails?.amount) +
                                              conFee
                                          ) || "$0.00"
                                        }`}
                                        className="w-full"
                                        isLoading={isBTNLoading}
                                        setIsLoading={setIsBTNLoading}
                                        handleClick={() => {
                                          setIsUpdate(false);
                                        }}
                                        ref={bankRef1}
                                        amount={paymentDetails?.amount}
                                        isDisabled={isFeeLoading}
                                        stripeBankURL={`${window.location.href}?drawer=open&amount=${paymentDetails?.amount}&method=bank&updateMethod=false&failedOrderId=${id}&checked=false`}
                                      />

                                      <BankPaymentButton
                                        buttonText={`Update and pay ${
                                          formatCurrency(
                                            parseFloat(paymentDetails?.amount) +
                                              conFee
                                          ) || "$0.00"
                                        }`}
                                        className="w-full"
                                        isLoading={isUpdateBTNLoading}
                                        setIsLoading={setIsUpdateBTNLoading}
                                        handleClick={() => {
                                          setIsUpdate(true);
                                        }}
                                        ref={bankRef}
                                        amount={paymentDetails?.amount}
                                        isDisabled={isFeeLoading}
                                        stripeBankURL={`${window.location.href}?drawer=open&amount=${paymentDetails?.amount}&method=bank&updateMethod=true&failedOrderId=${id}&checked=false`}
                                      />
                                    </div>
                                    <BackToPaymentMethodButton
                                      onClick={() => {
                                        setCurrentStep(1);
                                        setCompletedSteps([0]);
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="pt-10 w-full">
                                    <div className="py-2 flex justify-between items-center w-full gap-x-2">
                                      <Button
                                        text="Back"
                                        variant="white"
                                        className="max-w-44 w-full"
                                        onClick={handleBackStepClick}
                                      />

                                      <BankPaymentButton
                                        buttonText={`Pay ${
                                          formatCurrency(
                                            parseFloat(paymentDetails?.amount) +
                                              conFee
                                          ) || "$0.00"
                                        }`}
                                        className="w-44"
                                        isLoading={isBTNLoading}
                                        setIsLoading={setIsBTNLoading}
                                        handleClick={() => {
                                          setIsUpdate(false);
                                        }}
                                        ref={bankRef1}
                                        amount={paymentDetails?.amount}
                                        isDisabled={isFeeLoading}
                                        stripeBankURL={`${window.location.href}?drawer=open&amount=${paymentDetails?.amount}&method=bank&updateMethod=false&failedOrderId=${id}&checked=false`}
                                      />
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                        {/* after disclosure */}
                        <div
                          className={`max-h-[90%] h-full lg:px-0 px-5 relative w-full  ${
                            !showFinalDisclosure ? `hidden none` : ""
                          }`}
                        >
                          <DisclosureFinal
                            generalMarkers={generalMarkers}
                            handleAccept={handleAccept}
                            isDrawer={true}
                            isOpen={showFinalDisclosure}
                          />
                        </div>
                      </Elements>
                    ) : selectedMethod === "Card" &&
                      paymentPermission?.cc_gateway_name?.name ===
                        PaymentGateway.STRIPE &&
                      !isAddNewCard &&
                      cardStripePromise ? (
                      <Elements stripe={cardStripePromise}>
                        <div
                          className={
                            showFinalDisclosure
                              ? "hidden none"
                              : "h-calc(var(--shell-vh) - 90px)"
                          }
                        >
                          <div className="h-[92%] gap-7 lg:flex hidden">
                            <div className={`w-[10%] flex pt-2`}>
                              <CustomStepper2Step
                                currentStep={currentStep}
                                completedSteps={completedSteps}
                                className="!h-[50px]"
                                handleOnClick={handleOnClick}
                              />
                            </div>
                            <div className="flex flex-col gap-4 w-full">
                              <FailedPaymentComponent
                                order_id={id}
                                currentStep={currentStep}
                                isAddNewCard={isAddNewCard}
                                setIsAddNewCard={setIsAddNewCard}
                                dataPayment={dataPayment}
                                setDataPayment={setDataPayment}
                                walletList={oldWalletData}
                                setSavedCardsEmail={setSavedCardsEmail}
                                paymentMethodList={paymentMethodList}
                                setSelectedMethod={setSelectedMethod}
                                handleChangeCard={handleChangeCard}
                                setSelectedState={setSelectedState}
                                cardNumber={cardNumber}
                                selectedState={selectedState}
                                selectedMethod={selectedMethod}
                                iconType={iconType}
                                isDateErr={isDateErr}
                                handleChangeBank={handleChangeBank}
                                numberNotmatchedError={numberNotmatchedError}
                                setnumberNotmatchedError={
                                  setnumberNotmatchedError
                                }
                                bankName={bankName}
                                errMsg={errMsg}
                                isRingLoading={isRingLoading}
                                email={email}
                                setEmail={setEmail}
                                values={values}
                                paymentDetails={paymentDetails}
                                validateCard={() =>
                                  validateCard({
                                    values,
                                    iconType,
                                    setIsErr,
                                    setCardErrMsg,
                                    setErrMsg,
                                  })
                                }
                                isErr={isErr}
                                cardErrMsg={cardErrMsg}
                                setValues={setValues}
                                setGoogleTokenData={setGoogleTokenData}
                                setCurrentStep={setCurrentStep}
                                setCompletedSteps={setCompletedSteps}
                                createPayPalIntent={createPayPalIntent}
                                isGoogleLoading={isGoogleLoading}
                                setApplePaySecret={setApplePaySecret}
                                setApplePayOrder={setApplePayOrder}
                                setIsApplePay={setIsApplePay}
                                isDisabled={isDisabled}
                                isFeeLoading={isFeeLoading}
                                isSecretLoading={isSecretLoading}
                                futurePayment={futurePayment}
                                isManualForm={isManualForm}
                                setIsManualForm={setIsManualForm}
                                BankComp={
                                  <div className="pt-10">
                                    <StripeCardPaymentCheckoutForm
                                      values={values}
                                      handleChange={handleChangeCard}
                                      isEdit={false}
                                      iconType={iconType}
                                      selectedState={selectedState}
                                      setSelectedState={setSelectedState}
                                      setIconType={setIconType}
                                      cardErrMsg={cardErrMsg}
                                      amount={amount}
                                      email={email}
                                      setEmail={setEmail}
                                      isPaymentLink={true}
                                      isRetry={true}
                                      handleRetryNext={() => {
                                        setCurrentStep(2);
                                        setCompletedSteps([0, 1]);
                                      }}
                                      formCheck={formCheck}
                                      setFormCheck={setFormCheck}
                                      formEmptyCheck={formEmptyCheck}
                                      setFormEmptyCheck={setFormEmptyCheck}
                                    />
                                  </div>
                                }
                              />
                              {currentStep === 2 &&
                                (futurePayment ? (
                                  <div className="flex flex-col lg:gap-6 gap-4 justify-center items-center pt-10">
                                    <div className="w-full flex lg:flex-row flex-col gap-4">
                                      <CardPaymentButton
                                        orderId={
                                          decode(id || "")?.split("_")[0] || ""
                                        }
                                        buttonText={`Pay ${
                                          formatCurrency(
                                            parseFloat(paymentDetails?.amount) +
                                              conFee
                                          ) || "$0.00"
                                        }`}
                                        className="w-full"
                                        setCardErrMsg={setCardErrMsg}
                                        amount={paymentDetails?.amount}
                                        values={values}
                                        email={email}
                                        ref={cardRef1}
                                        handleClick={() => {
                                          setIsUpdate(false);
                                        }}
                                        isUpdateAndPay={false}
                                        isDisabled={isFeeLoading}
                                        verifyStripeCardPayment={
                                          verifyStripeCardPayment
                                        }
                                      />

                                      <CardPaymentButton
                                        orderId={
                                          decode(id || "")?.split("_")[0] || ""
                                        }
                                        buttonText={`Update and pay ${
                                          formatCurrency(
                                            parseFloat(paymentDetails?.amount) +
                                              conFee
                                          ) || "$0.00"
                                        }`}
                                        className="w-full"
                                        setCardErrMsg={setCardErrMsg}
                                        amount={paymentDetails?.amount}
                                        values={values}
                                        email={email}
                                        ref={cardRef}
                                        isUpdateAndPay={true}
                                        handleClick={() => {
                                          setIsUpdate(true);
                                        }}
                                        verifyStripeCardPayment={
                                          verifyStripeCardPayment
                                        }
                                        isDisabled={isFeeLoading}
                                      />
                                    </div>
                                    <p className="lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium">
                                      <span
                                        className="cursor-pointer"
                                        onClick={() => {
                                          setCurrentStep(1);
                                          setCompletedSteps([0]);
                                        }}
                                      >
                                        Back to payment method
                                      </span>
                                    </p>
                                  </div>
                                ) : (
                                  <div className="pt-10 w-full">
                                    <div className="py-2 flex justify-between items-center w-full gap-x-2">
                                      <Button
                                        text="Back"
                                        variant="white"
                                        className="max-w-44 w-full"
                                        onClick={handleBackStepClick}
                                      />

                                      <CardPaymentButton
                                        orderId={
                                          decode(id || "")?.split("_")[0] || ""
                                        }
                                        buttonText={`Pay ${
                                          formatCurrency(
                                            parseFloat(paymentDetails?.amount) +
                                              conFee
                                          ) || "$0.00"
                                        }`}
                                        amount={paymentDetails?.amount}
                                        setCardErrMsg={setCardErrMsg}
                                        className="w-44"
                                        values={values}
                                        email={email}
                                        ref={cardRef1}
                                        isUpdateAndPay={false}
                                        handleClick={() => {
                                          setIsUpdate(false);
                                        }}
                                        verifyStripeCardPayment={
                                          verifyStripeCardPayment
                                        }
                                        isDisabled={isFeeLoading}
                                      />
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                        <div
                          className={`max-h-[90%] h-full lg:px-0 px-5 relative w-full  ${
                            !showFinalDisclosure ? `hidden none` : ""
                          }`}
                        >
                          <DisclosureFinal
                            generalMarkers={generalMarkers}
                            handleAccept={handleAccept}
                            isDrawer={true}
                            isOpen={showFinalDisclosure}
                          />
                        </div>
                      </Elements>
                    ) : (
                      <>
                        <div
                          className={
                            showFinalDisclosure
                              ? "hidden none"
                              : "h-calc(var(--shell-vh) - 90px)"
                          }
                        >
                          <div className="h-[92%] gap-7 lg:flex hidden">
                            <div className={`w-[10%] flex pt-2`}>
                              <CustomStepper2Step
                                currentStep={currentStep}
                                completedSteps={completedSteps}
                                className="!h-[50px]"
                                handleOnClick={handleOnClick}
                              />
                            </div>
                            <div className="flex flex-col gap-4 w-full">
                              <FailedPaymentComponent
                                order_id={id}
                                currentStep={currentStep}
                                isAddNewCard={isAddNewCard}
                                setIsAddNewCard={setIsAddNewCard}
                                dataPayment={dataPayment}
                                setDataPayment={setDataPayment}
                                walletList={oldWalletData}
                                setSavedCardsEmail={setSavedCardsEmail}
                                paymentMethodList={paymentMethodList}
                                setSelectedMethod={setSelectedMethod}
                                handleChangeCard={handleChangeCard}
                                setSelectedState={setSelectedState}
                                cardNumber={cardNumber}
                                selectedState={selectedState}
                                selectedMethod={selectedMethod}
                                iconType={iconType}
                                isDateErr={isDateErr}
                                handleChangeBank={handleChangeBank}
                                numberNotmatchedError={numberNotmatchedError}
                                setnumberNotmatchedError={
                                  setnumberNotmatchedError
                                }
                                bankName={bankName}
                                errMsg={errMsg}
                                isRingLoading={isRingLoading}
                                email={email}
                                setEmail={setEmail}
                                values={values}
                                paymentDetails={paymentDetails}
                                validateCard={() =>
                                  validateCard({
                                    values,
                                    iconType,
                                    setIsErr,
                                    setCardErrMsg,
                                    setErrMsg,
                                  })
                                }
                                isErr={isErr}
                                cardErrMsg={cardErrMsg}
                                setValues={setValues}
                                setGoogleTokenData={setGoogleTokenData}
                                setCurrentStep={setCurrentStep}
                                setCompletedSteps={setCompletedSteps}
                                createPayPalIntent={createPayPalIntent}
                                isGoogleLoading={isGoogleLoading}
                                setApplePaySecret={setApplePaySecret}
                                setApplePayOrder={setApplePayOrder}
                                setIsApplePay={setIsApplePay}
                                isDisabled={isDisabled}
                                isFeeLoading={isFeeLoading}
                                isSecretLoading={isSecretLoading}
                                futurePayment={futurePayment}
                                isManualForm={isManualForm}
                                setIsManualForm={setIsManualForm}
                              />
                              {currentStep === 2 && (
                                <>
                                  {futurePayment ? (
                                    <div className="flex flex-col lg:gap-6 gap-4 justify-center items-center">
                                      <div className="w-full flex lg:flex-row flex-col gap-4">
                                        {selectedMethod === "Cash App Pay" &&
                                        clientSecret &&
                                        walletStripePromise ? (
                                          <Elements
                                            stripe={walletStripePromise}
                                            options={{ clientSecret }}
                                          >
                                            <CashAppForm
                                              isRecurring={false}
                                              cashAppURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=cashapp&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=false&checked=false`}
                                              isRetry={true}
                                              hasFuture={true}
                                              buttonText={`Pay ${formatCurrency(paymentDetails?.amount) || "$0.00"}`}
                                              amount={paymentDetails.amount}
                                              ref={cashAppRef1}
                                              isDisabled={isFeeLoading}
                                              handleClick={() => {
                                                setIsUpdate(false);
                                              }}
                                            />
                                          </Elements>
                                        ) : selectedMethod === "Amazon Pay" &&
                                          clientSecret &&
                                          amazonStripePromise ? (
                                          <Elements
                                            stripe={amazonStripePromise}
                                            options={{ clientSecret }}
                                          >
                                            <AmazonPayForm
                                              isRecurring={false}
                                              amount={paymentDetails?.amount}
                                              stripeAmazonPayURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=amazonPay&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=false&checked=false`}
                                              isRetry={true}
                                              hasFuture={true}
                                              buttonText={`Pay ${formatCurrency(paymentDetails?.amount) || "$0.00"}`}
                                              ref={amazonRef1}
                                              isDisabled={isFeeLoading}
                                              handleClick={() => {
                                                setIsUpdate(false);
                                              }}
                                            />
                                          </Elements>
                                        ) : selectedMethod === "Bank" &&
                                          paymentPermission?.ach_gateway_name
                                            ?.name !== PaymentGateway.STRIPE &&
                                          checkBankManualFormOrPlaid({
                                            manual_ach:
                                              portalSettings?.manual_ach,
                                            validate_bank_account:
                                              portalSettings?.validate_bank_account,
                                            isManualForm,
                                          }) ? (
                                          <PlaidLinkButton
                                            email={email}
                                            clusterId={
                                              decode(id || "")?.split("_")?.[1]
                                            }
                                            buttonText={`Pay ${
                                              formatCurrency(
                                                paymentDetails?.amount + conFee
                                              ) || "$0.00"
                                            }`}
                                            className="w-full"
                                            linkToken={linkToken}
                                            isLinkTokenLoading={
                                              isLinkTokenLoading
                                            }
                                            amount={paymentDetails?.amount}
                                            isLoading={isBTNLoading}
                                            bankRef={bankRef1}
                                            shouldUpdate={false}
                                            payment_agreement_opt={
                                              portalSettings?.payment_agreement_opt
                                            }
                                            setIsUpdate={setIsUpdate}
                                            handlePlaidBankPayment={
                                              handlePlaidBankPayment
                                            }
                                          />
                                        ) : (
                                          <Button
                                            text={`Pay ${
                                              formatCurrency(
                                                parseFloat(
                                                  paymentDetails?.amount
                                                ) + conFee
                                              ) || "$0.00"
                                            }`}
                                            className="w-full"
                                            isLoading={isBTNLoading}
                                            variant="variable"
                                            onClick={() => {
                                              handleOnSubmit(false);
                                            }}
                                          />
                                        )}

                                        {selectedMethod === "Cash App Pay" &&
                                        clientSecret &&
                                        walletStripePromise ? (
                                          <Elements
                                            stripe={walletStripePromise}
                                            options={{ clientSecret }}
                                          >
                                            <CashAppForm
                                              isRecurring={false}
                                              cashAppURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=cashapp&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=true&checked=false`}
                                              isRetry={true}
                                              hasFuture={true}
                                              buttonText={`Update and pay ${
                                                formatCurrency(
                                                  paymentDetails?.amount
                                                ) || "$0.00"
                                              }`}
                                              ref={cashAppRef}
                                              amount={paymentDetails.amount}
                                              isDisabled={isFeeLoading}
                                              handleClick={() => {
                                                setIsUpdate(true);
                                              }}
                                            />
                                          </Elements>
                                        ) : selectedMethod === "Amazon Pay" &&
                                          clientSecret &&
                                          amazonStripePromise ? (
                                          <Elements
                                            stripe={amazonStripePromise}
                                            options={{ clientSecret }}
                                          >
                                            <AmazonPayForm
                                              isRecurring={false}
                                              amount={paymentDetails?.amount}
                                              stripeAmazonPayURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=amazonPay&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=true&checked=false`}
                                              isRetry={true}
                                              hasFuture={true}
                                              buttonText={`Update and pay ${
                                                formatCurrency(
                                                  paymentDetails?.amount
                                                ) || "$0.00"
                                              }`}
                                              ref={amazonRef}
                                              isDisabled={isFeeLoading}
                                              handleClick={() => {
                                                setIsUpdate(true);
                                              }}
                                            />
                                          </Elements>
                                        ) : selectedMethod === "Bank" &&
                                          paymentPermission?.ach_gateway_name
                                            ?.name !== PaymentGateway.STRIPE &&
                                          checkBankManualFormOrPlaid({
                                            manual_ach:
                                              portalSettings?.manual_ach,
                                            validate_bank_account:
                                              portalSettings?.validate_bank_account,
                                            isManualForm,
                                          }) ? (
                                          <PlaidLinkButton
                                            email={email}
                                            clusterId={
                                              decode(id || "")?.split("_")?.[1]
                                            }
                                            buttonText={`Update and pay ${
                                              formatCurrency(
                                                paymentDetails?.amount + conFee
                                              ) || "$0.00"
                                            }`}
                                            className="w-full"
                                            linkToken={linkToken}
                                            amount={paymentDetails?.amount}
                                            isLinkTokenLoading={
                                              isLinkTokenLoading
                                            }
                                            isLoading={isUpdateBTNLoading}
                                            bankRef={bankRef}
                                            shouldUpdate={true}
                                            payment_agreement_opt={
                                              portalSettings?.payment_agreement_opt
                                            }
                                            setIsUpdate={setIsUpdate}
                                            handlePlaidBankPayment={
                                              handlePlaidBankPayment
                                            }
                                          />
                                        ) : (
                                          <Button
                                            text={`Update and pay ${
                                              formatCurrency(
                                                parseFloat(
                                                  paymentDetails?.amount
                                                ) + conFee
                                              ) || "$0.00"
                                            }`}
                                            className="w-full"
                                            isLoading={isUpdateBTNLoading}
                                            isDisabled={isFeeLoading}
                                            variant="variable"
                                            onClick={() => {
                                              handleOnSubmit(true);
                                            }}
                                          />
                                        )}
                                      </div>
                                      <p className="lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium">
                                        <span
                                          className="cursor-pointer"
                                          onClick={() => {
                                            setCurrentStep(1);
                                            setCompletedSteps([0]);
                                          }}
                                        >
                                          Back to payment method
                                        </span>
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="pt-10 w-full">
                                      <div className="py-2 flex justify-between items-center w-full gap-x-2">
                                        <Button
                                          text="Back"
                                          variant="white"
                                          className="max-w-44 w-full"
                                          onClick={handleBackStepClick}
                                        />
                                        {selectedMethod === "Cash App Pay" &&
                                        clientSecret &&
                                        walletStripePromise ? (
                                          <Elements
                                            stripe={walletStripePromise}
                                            options={{ clientSecret }}
                                          >
                                            <CashAppForm
                                              isRecurring={false}
                                              cashAppURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=cashapp&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=false&checked=false`}
                                              buttonText={`Pay ${formatCurrency(paymentDetails?.amount) || "$0.00"}`}
                                              isRetry={true}
                                              ref={cashAppRef1}
                                              amount={paymentDetails.amount}
                                              isDisabled={isFeeLoading}
                                              handleClick={() => {
                                                setIsUpdate(false);
                                              }}
                                            />
                                          </Elements>
                                        ) : selectedMethod === "Amazon Pay" &&
                                          clientSecret &&
                                          amazonStripePromise ? (
                                          <Elements
                                            stripe={amazonStripePromise}
                                            options={{ clientSecret }}
                                          >
                                            <AmazonPayForm
                                              isRecurring={false}
                                              amount={paymentDetails?.amount}
                                              stripeAmazonPayURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=amazonPay&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=false&checked=false`}
                                              buttonText={`Pay ${formatCurrency(paymentDetails?.amount) || "$0.00"}`}
                                              isRetry={true}
                                              ref={amazonRef1}
                                              isDisabled={isFeeLoading}
                                              handleClick={() => {
                                                setIsUpdate(false);
                                              }}
                                            />
                                          </Elements>
                                        ) : selectedMethod === "Bank" &&
                                          paymentPermission?.ach_gateway_name
                                            ?.name !== PaymentGateway.STRIPE &&
                                          checkBankManualFormOrPlaid({
                                            manual_ach:
                                              portalSettings?.manual_ach,
                                            validate_bank_account:
                                              portalSettings?.validate_bank_account,
                                            isManualForm,
                                          }) ? (
                                          <PlaidLinkButton
                                            email={email}
                                            clusterId={
                                              decode(id || "")?.split("_")?.[1]
                                            }
                                            buttonText={`Pay ${
                                              formatCurrency(
                                                paymentDetails?.amount + conFee
                                              ) || "$0.00"
                                            }`}
                                            className="max-w-44 w-full"
                                            linkToken={linkToken}
                                            amount={paymentDetails?.amount}
                                            isLinkTokenLoading={
                                              isLinkTokenLoading
                                            }
                                            isLoading={isBTNLoading}
                                            bankRef={bankRef1}
                                            shouldUpdate={false}
                                            payment_agreement_opt={
                                              portalSettings?.payment_agreement_opt
                                            }
                                            setIsUpdate={setIsUpdate}
                                            handlePlaidBankPayment={
                                              handlePlaidBankPayment
                                            }
                                          />
                                        ) : (
                                          <Button
                                            text={`Pay ${formatCurrency(paymentDetails?.amount + conFee) || "$0.00"}`}
                                            // isDisabled={isDisabled}
                                            className="max-w-44 w-full"
                                            isLoading={isBTNLoading}
                                            variant="variable"
                                            isDisabled={isFeeLoading}
                                            onClick={() => {
                                              handleOnSubmit(false);
                                            }}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div
                          className={`max-h-[90%] h-full lg:px-0 px-5 relative w-full  ${
                            !showFinalDisclosure ? `hidden none` : ""
                          }`}
                        >
                          <DisclosureFinal
                            generalMarkers={generalMarkers}
                            handleAccept={handleAccept}
                            isDrawer={true}
                            isOpen={showFinalDisclosure}
                          />
                        </div>
                      </>
                    )}

                    {/* mobile and tablet view */}
                    <div
                      className={`lg:hidden ${showFinalDisclosure ? "hidden" : "flex"} flex-col px-5 w-full`}
                    >
                      <div className="py-5 w-full">
                        <Stepper
                          currentStep={currentStep}
                          Steps={2}
                          completedSteps={completedSteps}
                          handleOnClick={handleOnClick}
                        />
                      </div>

                      {bankSecret &&
                      selectedMethod === "Bank" &&
                      paymentPermission?.ach_gateway_name.name ===
                        PaymentGateway.STRIPE &&
                      !isAddNewCard ? (
                        <Elements
                          stripe={bankStripePromise}
                          options={bankFormOptions}
                        >
                          <FailedPaymentComponent
                            order_id={id}
                            currentStep={currentStep}
                            isAddNewCard={isAddNewCard}
                            setIsAddNewCard={setIsAddNewCard}
                            dataPayment={dataPayment}
                            setDataPayment={setDataPayment}
                            walletList={oldWalletData}
                            setSavedCardsEmail={setSavedCardsEmail}
                            paymentMethodList={paymentMethodList}
                            setSelectedMethod={setSelectedMethod}
                            handleChangeCard={handleChangeCard}
                            setSelectedState={setSelectedState}
                            cardNumber={cardNumber}
                            selectedState={selectedState}
                            selectedMethod={selectedMethod}
                            iconType={iconType}
                            isDateErr={isDateErr}
                            handleChangeBank={handleChangeBank}
                            numberNotmatchedError={numberNotmatchedError}
                            setnumberNotmatchedError={setnumberNotmatchedError}
                            bankName={bankName}
                            errMsg={errMsg}
                            isRingLoading={isRingLoading}
                            email={email}
                            setEmail={setEmail}
                            values={values}
                            paymentDetails={paymentDetails}
                            validateCard={() =>
                              validateCard({
                                values,
                                iconType,
                                setIsErr,
                                setCardErrMsg,
                                setErrMsg,
                              })
                            }
                            isErr={isErr}
                            cardErrMsg={cardErrMsg}
                            setValues={setValues}
                            setGoogleTokenData={setGoogleTokenData}
                            setCurrentStep={setCurrentStep}
                            setCompletedSteps={setCompletedSteps}
                            createPayPalIntent={createPayPalIntent}
                            isGoogleLoading={isGoogleLoading}
                            setApplePaySecret={setApplePaySecret}
                            setApplePayOrder={setApplePayOrder}
                            setIsApplePay={setIsApplePay}
                            isDisabled={isDisabled}
                            isFeeLoading={isFeeLoading}
                            isSecretLoading={isSecretLoading}
                            futurePayment={futurePayment}
                            isManualForm={isManualForm}
                            setIsManualForm={setIsManualForm}
                            BankComp={
                              <div className="pt-10">
                                <StripeBankForm
                                  amount={paymentDetails?.amount}
                                  email={email}
                                  setEmail={setEmail}
                                  isSecretLoading={isLoading}
                                  isRetry={true}
                                  setIsDisable={setIsDisabled}
                                />
                              </div>
                            }
                          />
                          {currentStep === 2 &&
                            (futurePayment ? (
                              <div className="flex flex-col lg:gap-6 gap-4 justify-center items-center pt-10">
                                <div className="w-full flex lg:flex-row flex-col gap-4">
                                  <BankPaymentButton
                                    buttonText={`Pay ${
                                      formatCurrency(
                                        parseFloat(paymentDetails?.amount) +
                                          conFee
                                      ) || "$0.00"
                                    }`}
                                    className="w-full"
                                    isLoading={isBTNLoading}
                                    setIsLoading={setIsBTNLoading}
                                    handleClick={() => {
                                      setIsUpdate(false);
                                    }}
                                    ref={bankRef1}
                                    amount={paymentDetails?.amount}
                                    stripeBankURL={`${window.location.href}?drawer=open&amount=${paymentDetails?.amount}&method=bank&updateMethod=false&failedOrderId=${id}&checked=false`}
                                  />

                                  <BankPaymentButton
                                    buttonText={`Update and pay ${
                                      formatCurrency(
                                        parseFloat(paymentDetails?.amount) +
                                          conFee
                                      ) || "$0.00"
                                    }`}
                                    className="w-full"
                                    isLoading={isUpdateBTNLoading}
                                    setIsLoading={setIsUpdateBTNLoading}
                                    handleClick={() => {
                                      setIsUpdate(true);
                                    }}
                                    ref={bankRef}
                                    amount={paymentDetails?.amount}
                                    stripeBankURL={`${window.location.href}?drawer=open&amount=${paymentDetails?.amount}&method=bank&updateMethod=true&failedOrderId=${id}&checked=false`}
                                  />
                                </div>
                                <BackToPaymentMethodButton
                                  onClick={() => {
                                    setCurrentStep(1);
                                    setCompletedSteps([0]);
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="pt-10 w-full">
                                <div className="py-2 flex justify-between items-center w-full gap-x-2">
                                  <Button
                                    text="Back"
                                    variant="white"
                                    className="max-w-44 w-full"
                                    onClick={handleBackStepClick}
                                  />

                                  <BankPaymentButton
                                    buttonText={`Pay ${formatCurrency(paymentDetails?.amount) || "$0.00"}`}
                                    className="w-full"
                                    isLoading={isBTNLoading}
                                    setIsLoading={setIsBTNLoading}
                                    handleClick={() => {
                                      setIsUpdate(false);
                                    }}
                                    ref={bankRef1}
                                    amount={paymentDetails?.amount}
                                    stripeBankURL={`${window.location.href}?drawer=open&amount=${paymentDetails?.amount}&method=bank&updateMethod=false&failedOrderId=${id}&checked=false`}
                                  />
                                </div>
                              </div>
                            ))}
                        </Elements>
                      ) : selectedMethod === "Card" &&
                        paymentPermission?.cc_gateway_name?.name ===
                          PaymentGateway.STRIPE &&
                        !isAddNewCard ? (
                        <Elements stripe={cardStripePromise}>
                          <FailedPaymentComponent
                            order_id={id}
                            currentStep={currentStep}
                            isAddNewCard={isAddNewCard}
                            setIsAddNewCard={setIsAddNewCard}
                            dataPayment={dataPayment}
                            setDataPayment={setDataPayment}
                            walletList={oldWalletData}
                            setSavedCardsEmail={setSavedCardsEmail}
                            paymentMethodList={paymentMethodList}
                            setSelectedMethod={setSelectedMethod}
                            handleChangeCard={handleChangeCard}
                            setSelectedState={setSelectedState}
                            cardNumber={cardNumber}
                            selectedState={selectedState}
                            selectedMethod={selectedMethod}
                            iconType={iconType}
                            isDateErr={isDateErr}
                            handleChangeBank={handleChangeBank}
                            numberNotmatchedError={numberNotmatchedError}
                            setnumberNotmatchedError={setnumberNotmatchedError}
                            bankName={bankName}
                            errMsg={errMsg}
                            isRingLoading={isRingLoading}
                            email={email}
                            setEmail={setEmail}
                            values={values}
                            paymentDetails={paymentDetails}
                            validateCard={() =>
                              validateCard({
                                values,
                                iconType,
                                setIsErr,
                                setCardErrMsg,
                                setErrMsg,
                              })
                            }
                            isErr={isErr}
                            cardErrMsg={cardErrMsg}
                            setValues={setValues}
                            setGoogleTokenData={setGoogleTokenData}
                            setCurrentStep={setCurrentStep}
                            setCompletedSteps={setCompletedSteps}
                            createPayPalIntent={createPayPalIntent}
                            isGoogleLoading={isGoogleLoading}
                            setApplePaySecret={setApplePaySecret}
                            setApplePayOrder={setApplePayOrder}
                            setIsApplePay={setIsApplePay}
                            isDisabled={isDisabled}
                            isFeeLoading={isFeeLoading}
                            isSecretLoading={isSecretLoading}
                            futurePayment={futurePayment}
                            isManualForm={isManualForm}
                            setIsManualForm={setIsManualForm}
                            BankComp={
                              <div className="pt-10">
                                <StripeCardPaymentCheckoutForm
                                  values={values}
                                  handleChange={handleChangeCard}
                                  isEdit={false}
                                  iconType={iconType}
                                  setIconType={setIconType}
                                  selectedState={selectedState}
                                  setSelectedState={setSelectedState}
                                  cardErrMsg={cardErrMsg}
                                  amount={amount}
                                  email={email}
                                  setEmail={setEmail}
                                  isPaymentLink={true}
                                  isRetry={true}
                                  handleRetryNext={() => {
                                    setCurrentStep(2);
                                    setCompletedSteps([0, 1]);
                                  }}
                                  formCheck={formCheck}
                                  setFormCheck={setFormCheck}
                                  formEmptyCheck={formEmptyCheck}
                                  setFormEmptyCheck={setFormEmptyCheck}
                                />
                              </div>
                            }
                          />
                          {currentStep === 2 &&
                            (futurePayment ? (
                              <div className="flex flex-col lg:gap-6 gap-4 justify-center items-center pt-10">
                                <div className="w-full flex lg:flex-row flex-col gap-4">
                                  <CardPaymentButton
                                    orderId={
                                      decode(id || "")?.split("_")[0] || ""
                                    }
                                    buttonText={`Pay ${
                                      formatCurrency(
                                        parseFloat(paymentDetails?.amount) +
                                          conFee
                                      ) || "$0.00"
                                    }`}
                                    className="w-full"
                                    amount={paymentDetails?.amount}
                                    setCardErrMsg={setCardErrMsg}
                                    values={values}
                                    email={email}
                                    ref={cardRef1}
                                    isUpdateAndPay={false}
                                    handleClick={() => {
                                      setIsUpdate(false);
                                    }}
                                    verifyStripeCardPayment={
                                      verifyStripeCardPayment
                                    }
                                  />

                                  <CardPaymentButton
                                    orderId={
                                      decode(id || "")?.split("_")[0] || ""
                                    }
                                    buttonText={`Update and pay ${
                                      formatCurrency(
                                        parseFloat(paymentDetails?.amount) +
                                          conFee
                                      ) || "$0.00"
                                    }`}
                                    className="w-full"
                                    amount={paymentDetails?.amount}
                                    setCardErrMsg={setCardErrMsg}
                                    values={values}
                                    email={email}
                                    ref={cardRef}
                                    isUpdateAndPay={true}
                                    handleClick={() => {
                                      setIsUpdate(true);
                                    }}
                                    verifyStripeCardPayment={
                                      verifyStripeCardPayment
                                    }
                                  />
                                </div>
                                <p className="lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium">
                                  <span
                                    className="cursor-pointer"
                                    onClick={() => {
                                      setCurrentStep(1);
                                      setCompletedSteps([0]);
                                    }}
                                  >
                                    Back to payment method
                                  </span>
                                </p>
                              </div>
                            ) : (
                              <div className="pt-10 w-full">
                                <div className="py-2 flex justify-between items-center w-full gap-x-2">
                                  <Button
                                    text="Back"
                                    variant="white"
                                    className="max-w-44 w-full"
                                    onClick={handleBackStepClick}
                                  />

                                  <CardPaymentButton
                                    orderId={
                                      decode(id || "")?.split("_")[0] || ""
                                    }
                                    buttonText={`Pay ${
                                      formatCurrency(
                                        parseFloat(paymentDetails?.amount) +
                                          conFee
                                      ) || "$0.00"
                                    }`}
                                    amount={paymentDetails?.amount}
                                    setCardErrMsg={setCardErrMsg}
                                    className="w-full"
                                    values={values}
                                    email={email}
                                    ref={cardRef1}
                                    isUpdateAndPay={false}
                                    handleClick={() => {
                                      setIsUpdate(false);
                                    }}
                                    verifyStripeCardPayment={
                                      verifyStripeCardPayment
                                    }
                                  />
                                </div>
                              </div>
                            ))}
                        </Elements>
                      ) : (
                        <>
                          <FailedPaymentComponent
                            order_id={id}
                            currentStep={currentStep}
                            isAddNewCard={isAddNewCard}
                            setIsAddNewCard={setIsAddNewCard}
                            dataPayment={dataPayment}
                            setDataPayment={setDataPayment}
                            walletList={oldWalletData}
                            setSavedCardsEmail={setSavedCardsEmail}
                            paymentMethodList={paymentMethodList}
                            setSelectedMethod={setSelectedMethod}
                            handleChangeCard={handleChangeCard}
                            setSelectedState={setSelectedState}
                            cardNumber={cardNumber}
                            selectedState={selectedState}
                            selectedMethod={selectedMethod}
                            iconType={iconType}
                            isDateErr={isDateErr}
                            handleChangeBank={handleChangeBank}
                            numberNotmatchedError={numberNotmatchedError}
                            setnumberNotmatchedError={setnumberNotmatchedError}
                            bankName={bankName}
                            errMsg={errMsg}
                            isRingLoading={isRingLoading}
                            email={email}
                            setEmail={setEmail}
                            values={values}
                            paymentDetails={paymentDetails}
                            validateCard={() =>
                              validateCard({
                                values,
                                iconType,
                                setIsErr,
                                setCardErrMsg,
                                setErrMsg,
                              })
                            }
                            isErr={isErr}
                            cardErrMsg={cardErrMsg}
                            setValues={setValues}
                            setGoogleTokenData={setGoogleTokenData}
                            setCurrentStep={setCurrentStep}
                            setCompletedSteps={setCompletedSteps}
                            createPayPalIntent={createPayPalIntent}
                            isGoogleLoading={isGoogleLoading}
                            setApplePaySecret={setApplePaySecret}
                            setApplePayOrder={setApplePayOrder}
                            setIsApplePay={setIsApplePay}
                            isDisabled={isDisabled}
                            isFeeLoading={isFeeLoading}
                            isSecretLoading={isSecretLoading}
                            isManualForm={isManualForm}
                            setIsManualForm={setIsManualForm}
                            futurePayment={futurePayment}
                          />
                          {currentStep === 2 && (
                            <>
                              {futurePayment ? (
                                <div className="flex flex-col lg:gap-6 gap-4 justify-center items-center">
                                  <div className="w-full flex lg:flex-row flex-col gap-4">
                                    {selectedMethod === "Cash App Pay" &&
                                    clientSecret ? (
                                      <Elements
                                        stripe={walletStripePromise}
                                        options={{ clientSecret }}
                                      >
                                        <CashAppForm
                                          isRecurring={false}
                                          cashAppURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=cashapp&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=false&checked=false`}
                                          isRetry={true}
                                          hasFuture={true}
                                          amount={paymentDetails.amount}
                                          buttonText={`Pay ${formatCurrency(paymentDetails?.amount) || "$0.00"}`}
                                          ref={cashAppRef1}
                                          handleClick={() => {
                                            setIsUpdate(false);
                                          }}
                                        />
                                      </Elements>
                                    ) : selectedMethod === "Amazon Pay" &&
                                      clientSecret ? (
                                      <Elements
                                        stripe={amazonStripePromise}
                                        options={{ clientSecret }}
                                      >
                                        <AmazonPayForm
                                          isRecurring={false}
                                          amount={paymentDetails?.amount}
                                          stripeAmazonPayURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=amazonPay&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=false&checked=false`}
                                          isRetry={true}
                                          hasFuture={true}
                                          buttonText={`Pay ${formatCurrency(paymentDetails?.amount) || "$0.00"}`}
                                          ref={amazonRef1}
                                          handleClick={() => {
                                            setIsUpdate(false);
                                          }}
                                        />
                                      </Elements>
                                    ) : selectedMethod === "Bank" &&
                                      paymentPermission?.ach_gateway_name
                                        ?.name !== PaymentGateway.STRIPE &&
                                      checkBankManualFormOrPlaid({
                                        manual_ach: portalSettings?.manual_ach,
                                        validate_bank_account:
                                          portalSettings?.validate_bank_account,
                                        isManualForm,
                                      }) ? (
                                      <PlaidLinkButton
                                        email={email}
                                        clusterId={
                                          decode(id || "")?.split("_")?.[1]
                                        }
                                        buttonText={`Pay ${formatCurrency(paymentDetails?.amount + conFee) || "$0.00"}`}
                                        className="w-full"
                                        linkToken={linkToken}
                                        isLinkTokenLoading={isLinkTokenLoading}
                                        isLoading={isBTNLoading}
                                        amount={paymentDetails?.amount}
                                        bankRef={bankRef1}
                                        shouldUpdate={false}
                                        payment_agreement_opt={
                                          portalSettings?.payment_agreement_opt
                                        }
                                        setIsUpdate={setIsUpdate}
                                        handlePlaidBankPayment={
                                          handlePlaidBankPayment
                                        }
                                      />
                                    ) : (
                                      <Button
                                        text={`Pay ${
                                          formatCurrency(
                                            parseFloat(paymentDetails?.amount) +
                                              conFee
                                          ) || "$0.00"
                                        }`}
                                        className="w-full"
                                        isLoading={isBTNLoading}
                                        variant="variable"
                                        onClick={() => {
                                          handleOnSubmit(false);
                                        }}
                                      />
                                    )}

                                    {selectedMethod === "Cash App Pay" &&
                                    clientSecret ? (
                                      <Elements
                                        stripe={walletStripePromise}
                                        options={{ clientSecret }}
                                      >
                                        <CashAppForm
                                          isRecurring={false}
                                          cashAppURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=cashapp&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=true&checked=false`}
                                          isRetry={true}
                                          hasFuture={true}
                                          amount={paymentDetails.amount}
                                          buttonText={`Update and pay ${
                                            formatCurrency(
                                              paymentDetails?.amount
                                            ) || "$0.00"
                                          }`}
                                          ref={cashAppRef}
                                          handleClick={() => {
                                            setIsUpdate(true);
                                          }}
                                        />
                                      </Elements>
                                    ) : selectedMethod === "Amazon Pay" &&
                                      clientSecret ? (
                                      <Elements
                                        stripe={amazonStripePromise}
                                        options={{ clientSecret }}
                                      >
                                        <AmazonPayForm
                                          isRecurring={false}
                                          amount={paymentDetails?.amount}
                                          stripeAmazonPayURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=amazonPay&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=true&checked=false`}
                                          isRetry={true}
                                          hasFuture={true}
                                          buttonText={`Update and pay ${
                                            formatCurrency(
                                              paymentDetails?.amount
                                            ) || "$0.00"
                                          }`}
                                          ref={amazonRef}
                                          handleClick={() => {
                                            setIsUpdate(true);
                                          }}
                                        />
                                      </Elements>
                                    ) : selectedMethod === "Bank" &&
                                      paymentPermission?.ach_gateway_name
                                        ?.name !== PaymentGateway.STRIPE &&
                                      checkBankManualFormOrPlaid({
                                        manual_ach: portalSettings?.manual_ach,
                                        validate_bank_account:
                                          portalSettings?.validate_bank_account,
                                        isManualForm,
                                      }) ? (
                                      <PlaidLinkButton
                                        email={email}
                                        clusterId={
                                          decode(id || "")?.split("_")?.[1]
                                        }
                                        buttonText={`Update and pay ${
                                          formatCurrency(
                                            paymentDetails?.amount + conFee
                                          ) || "$0.00"
                                        }`}
                                        className="w-full"
                                        linkToken={linkToken}
                                        isLinkTokenLoading={isLinkTokenLoading}
                                        isLoading={isUpdateBTNLoading}
                                        amount={paymentDetails?.amount}
                                        bankRef={bankRef}
                                        shouldUpdate={true}
                                        payment_agreement_opt={
                                          portalSettings?.payment_agreement_opt
                                        }
                                        setIsUpdate={setIsUpdate}
                                        handlePlaidBankPayment={
                                          handlePlaidBankPayment
                                        }
                                      />
                                    ) : (
                                      <Button
                                        text={`Update and pay ${
                                          formatCurrency(
                                            parseFloat(paymentDetails?.amount) +
                                              conFee
                                          ) || "$0.00"
                                        }`}
                                        className="w-full"
                                        isLoading={isUpdateBTNLoading}
                                        variant="variable"
                                        onClick={() => {
                                          handleOnSubmit(true);
                                        }}
                                      />
                                    )}
                                  </div>
                                  <p className="lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium">
                                    <span
                                      className="cursor-pointer"
                                      onClick={() => {
                                        setCurrentStep(1);
                                        setCompletedSteps([0]);
                                      }}
                                    >
                                      Back to payment method
                                    </span>
                                  </p>
                                </div>
                              ) : (
                                <div className="pt-10 w-full">
                                  <div className="py-2 flex justify-between items-center w-full gap-x-2">
                                    <Button
                                      text="Back"
                                      variant="white"
                                      className="max-w-44 w-full"
                                      onClick={handleBackStepClick}
                                    />
                                    {selectedMethod === "Cash App Pay" &&
                                    clientSecret ? (
                                      <Elements
                                        stripe={walletStripePromise}
                                        options={{ clientSecret }}
                                      >
                                        <CashAppForm
                                          isRecurring={false}
                                          buttonText={`Pay ${formatCurrency(paymentDetails?.amount) || "$0.00"}`}
                                          cashAppURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=cashapp&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=false&checked=false`}
                                          isRetry={true}
                                          amount={paymentDetails.amount}
                                          ref={cashAppRef1}
                                          handleClick={() => {
                                            setIsUpdate(false);
                                          }}
                                        />
                                      </Elements>
                                    ) : selectedMethod === "Amazon Pay" &&
                                      clientSecret ? (
                                      <Elements
                                        stripe={amazonStripePromise}
                                        options={{ clientSecret }}
                                      >
                                        <AmazonPayForm
                                          isRecurring={false}
                                          amount={paymentDetails?.amount}
                                          buttonText={`Pay ${formatCurrency(paymentDetails?.amount) || "$0.00"}`}
                                          stripeAmazonPayURL={`${window.location.href}?drawer=open&failedOrderId=${id}&method=amazonPay&amount=${paymentDetails?.amount}&isRecurring=false&updateMethod=false&checked=false`}
                                          isRetry={true}
                                          ref={amazonRef1}
                                          handleClick={() => {
                                            setIsUpdate(false);
                                          }}
                                        />
                                      </Elements>
                                    ) : selectedMethod === "Bank" &&
                                      paymentPermission?.ach_gateway_name
                                        ?.name !== PaymentGateway.STRIPE &&
                                      checkBankManualFormOrPlaid({
                                        manual_ach: portalSettings?.manual_ach,
                                        validate_bank_account:
                                          portalSettings?.validate_bank_account,
                                        isManualForm,
                                      }) ? (
                                      <PlaidLinkButton
                                        email={email}
                                        clusterId={
                                          decode(id || "")?.split("_")?.[1]
                                        }
                                        buttonText={`Pay ${formatCurrency(paymentDetails?.amount + conFee) || "$0.00"}`}
                                        className="max-w-44 w-full"
                                        linkToken={linkToken}
                                        isLinkTokenLoading={isLinkTokenLoading}
                                        isLoading={isBTNLoading}
                                        amount={paymentDetails?.amount}
                                        bankRef={bankRef1}
                                        shouldUpdate={false}
                                        payment_agreement_opt={
                                          portalSettings?.payment_agreement_opt
                                        }
                                        setIsUpdate={setIsUpdate}
                                        handlePlaidBankPayment={
                                          handlePlaidBankPayment
                                        }
                                      />
                                    ) : (
                                      <Button
                                        text={`Pay ${formatCurrency(paymentDetails?.amount + conFee) || "$0.00"}`}
                                        className="max-w-44 w-full"
                                        isLoading={isBTNLoading}
                                        variant="variable"
                                        onClick={() => {
                                          handleOnSubmit(false);
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="lg:h-[92%] h-[90%] lg:px-0 px-5">
                    <DisclosureInitial
                      isOpen={true}
                      handleAccept={() => setInitialDisclosureAccept(true)}
                      generalMarkers={generalMarkers}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <Drawer
        enableOverlay={true}
        direction="right"
        open={isPreview}
        size={`${isLessThan680 ? `100vw` : "680px"}`}
        onClose={() => {
          setUpdatedData(null);
          setIsPreview(false);
        }}
        overlayColor="#000"
        lockBackgroundScroll={true}
      >
        <FailedPaymentPreview
          updatedData={updatedData}
          setUpdateData={setUpdatedData}
          setIsPreview={setIsPreview}
          isPreviewId={isPreviewId}
          isPreview={isPreview}
          isScheduled={false}
          companyLogo={companyLogo}
        />
      </Drawer>

      <CustomModal
        signModal={false}
        isLoading={false}
        isOpen={isDuplicateOrders && duplicateOrdersCount > 0}
        title="Alert"
        doneText="Complete Payment"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        cancleText="Cancel"
        onDoneClick={() => {
          dispatch(resetDuplicateOrder());
          handleAccept();
        }}
        onCloseClick={() => {
          dispatch(resetCheckDuplicatePayment());
          dispatch(resetDuplicateOrder());
        }}
        children={
          <React.Fragment>
            <div className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
              A similar payment was made in the last 10 minutes. Click 'Complete
              Payment' if this is not a duplicate.
            </div>
          </React.Fragment>
        }
      />

      <CustomModal
        signModal={false}
        isLoading={false}
        isOpen={showNavigationModal}
        title="Alert"
        doneText="Leave"
        cancleText="Stay"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        onDoneClick={handleConfirmLeave}
        onCloseClick={handleCancelLeave}
      >
        <div className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
          Are you sure you want to leave the page? Your payment is still being
          processed.
        </div>
      </CustomModal>
    </>
  );
};

const PlaidLinkButton = ({
  buttonText,
  className,
  linkToken,
  isLinkTokenLoading,
  bankRef,
  payment_agreement_opt,
  setIsUpdate,
  shouldUpdate,
  handlePlaidBankPayment,
  isLoading,
  clusterId = "",
  email = "",
  amount = 0,
}: {
  buttonText: string;
  className: string;
  linkToken: string | null;
  isLinkTokenLoading: boolean;
  bankRef: any;
  payment_agreement_opt: "C" | "D" | "O";
  setIsUpdate: (val: boolean) => void;
  shouldUpdate: boolean;
  isLoading: boolean;
  clusterId?: string;
  email?: string;
  amount?: number;
  handlePlaidBankPayment: (args: {
    value: any;
    isCCTokenAvailable: boolean;
    updateMethod: boolean;
  }) => void;
}) => {
  return (
    <>
      {!linkToken ? (
        <Button
          text={buttonText}
          className={className}
          isDisabled={true}
          isLoading={isLinkTokenLoading || isLoading}
          variant="variable"
        />
      ) : (
        <LinkForm
          buttonText={buttonText}
          className={className}
          linkToken={linkToken || ""}
          isLoading={isLinkTokenLoading || isLoading}
          ref={bankRef}
          payment_agreement_opt={payment_agreement_opt}
          setIsUpdate={setIsUpdate}
          shouldUpdate={shouldUpdate}
          handlePlaidBankPayment={handlePlaidBankPayment}
          clusterId={clusterId}
          email={email}
          amount={amount}
        />
      )}
    </>
  );
};

const LinkForm = forwardRef(
  (
    {
      buttonText,
      className,
      linkToken,
      isLoading,
      payment_agreement_opt,
      setIsUpdate,
      shouldUpdate = false,
      clusterId = "",
      handlePlaidBankPayment,
      email = "",
      amount = 0,
    }: {
      buttonText: string;
      className: string;
      linkToken: string;
      isLoading: boolean;
      clusterId?: string;
      payment_agreement_opt: "C" | "D" | "O";
      setIsUpdate: (val: boolean) => void;
      shouldUpdate: boolean;
      email: string;
      amount?: number;
      handlePlaidBankPayment: (args: {
        value: any;
        isCCTokenAvailable: boolean;
        updateMethod: boolean;
      }) => void;
    },
    ref
  ) => {
    const [isMakingPayment, setIsMakingPayment] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const userData = useUserData();
    const { checkedDuplicatePayment } = useAppSelector(
      (state: RootState) => state.payment
    );
    const showFinalDisclosure = useShowFinalDisclosure();

    const { clusterId: cluster_id } = useParams();

    const getPaymentToken = async (data: any) => {
      setIsMakingPayment(true);
      try {
        let clusterid = clusterId;

        let payload = {
          public_token: data.publicToken,
          bankAccountID: data.accountId,
          clusterid: clusterid,
          accountid: userData?.data?.accountData?.accountid,
          slug: cluster_id || "",
        };
        let res: any = await dispatch(getPlaidPaymentToken(payload)).unwrap();
        if (res?.body?.success) {
          const { address } = res?.body?.data;
          let value = {
            bankName: data?.bankName,
            bank: data?.bankName,
            choldername: userData?.data?.accountData?.first_name,
            routing_number: res?.body?.data?.routing_numebr,
            account_number: res?.body?.data?.account_number,
            gateway_name: res?.body?.data?.gateway_name,
            x_city: address?.city ?? "",
            x_address: address?.street ?? "",
            address2: "",
            x_zip: address?.postal_code ?? "",
            x_state: address?.region ?? "",
            x_country: address?.country ?? "",
            email,
            gateway_id: res?.body?.data?.gatewayId,
            cc_token: res?.body?.data?.cc_token,
            cc_token2: res?.body?.data?.cc_token2,
            pc_atid: res?.body?.data?.pc_atid,
            cppid: res?.body?.data?.cppid,
          };
          let isCCTokenAvailable =
            res?.body?.data?.cc_token &&
            res?.body?.data?.cc_token?.trim() !== "" &&
            res?.body?.data?.gateway_name === PaymentGateway.ACHQ;
          await handlePlaidBankPayment({
            value,
            isCCTokenAvailable,
            updateMethod: shouldUpdate,
          });
        }
      } catch (error) {
        console.log("getPaymentTokenerror =====", error);
      } finally {
        setIsMakingPayment(false);
      }
    };

    const onSuccess = useCallback(
      async (public_token?: string, metadata?: any) => {
        if (public_token && metadata) {
          let data = {
            publicToken: public_token,
            accountId: metadata?.account_id,
            bankName: metadata?.institution?.name,
          };
          await getPaymentToken(data);
        }
      },
      []
    );

    const config: Parameters<typeof usePlaidLink>[0] = {
      token: linkToken!,
      onSuccess: onSuccess,
      onExit: (error: PlaidLinkError | null) => {
        console.log("error =====", error);
      },
    };

    const { open, ready } = usePlaidLink(config);

    const checkPastAmount = async () => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id,
          payment_method: "ACH",
          amount: amount,
        };
        let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
        const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
        if (isDuplicateOrders && duplicateOrdersCount > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleCheckDisclosureAndDuplicate = async () => {
      try {
        let moveAhead = !checkedDuplicatePayment
          ? await checkPastAmount()
          : true;
        if (!moveAhead) {
          dispatch(onSetFinalDisclosureLoading(false));
          setIsUpdate(shouldUpdate);
          return false;
        } else {
          if (
            (payment_agreement_opt === SettingsValue.CUSTOM ||
              payment_agreement_opt === SettingsValue.DEFAULT) &&
            !showFinalDisclosure
          ) {
            dispatch(onSetFinalDisclosureLoading(false));
            dispatch(resetDuplicateOrder());
            dispatch(onSetShowDisclosure(true));
            setIsUpdate(shouldUpdate);
          } else {
            dispatch(resetDuplicateOrder());
            dispatch(resetCheckDuplicatePayment());
            return true;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handlePlaidBank = async () => {
      setIsMakingPayment(true);
      try {
        let moveAhead = await handleCheckDisclosureAndDuplicate();
        if (!moveAhead) {
          setIsMakingPayment(false);
          return;
        }
        open();
      } catch (error) {
        console.log("error =====", error);
      }
    };

    useImperativeHandle(ref, () => ({
      callHandleClick: () => handlePlaidBank(),
    }));

    return (
      <Button
        text={buttonText}
        className={className}
        isDisabled={!ready}
        isLoading={isLoading || isMakingPayment}
        variant="variable"
        onClick={() => {
          handlePlaidBank();
        }}
      />
    );
  }
);

export default FailedPayment;
